import { artifactDownloadPageURL, artifactDownloadPath } from "./artifact-download"

export type CoworkProject = {
  id: string
  company_id: string
  owner_user_id: string
  name: string
  description?: string | null
  status: string
  default_workflow_id?: string | null
  default_period_id?: string | null
  project_metadata: Record<string, unknown>
  project_role: "viewer" | "editor" | "owner"
  created_at: string
  updated_at: string
}

export type CoworkCompany = {
  id: string
  name: string
  status: string
  role: string
}

export type CoworkCompanySummary = CoworkCompany & {
  country?: string | null
  registration_number?: string | null
  vat_number?: string | null
  projects: number
  files: number
  reports: number
  active_workflows: number
  last_activity?: string | null
}

export type CoworkCompanyAccountingProfile = {
  company_id: string
  jurisdiction?: string | null
  base_currency?: string | null
  fiscal_year_start?: string | null
  fiscal_year_end?: string | null
  vat_number?: string | null
  vat_reporting_frequency?: "monthly" | "quarterly" | "not_registered" | null
  reporting_framework?: string | null
  business_activity: Record<string, unknown>
  direct_cost_rules: string[]
  operating_expense_rules: string[]
  selling_expense_rules: string[]
  capitalization_rules: Array<Record<string, unknown> | string>
  account_mappings: Record<string, unknown>
  expense_taxonomy: Record<string, unknown>
  fixed_asset_taxonomy: Record<string, unknown>
  pnl_taxonomy: Record<string, unknown>
  materiality: Record<string, unknown>
  metadata: Record<string, unknown>
}

export type CoworkWorkspaceMember = {
  user_id: string
  email?: string | null
  name?: string | null
  username?: string | null
  role: "viewer" | "member" | "admin" | "owner"
  status: string
  company_ids: string[]
}

export type CoworkWorkspaceInvitation = {
  id: string
  email: string
  role: "viewer" | "member" | "admin"
  status: string
  invited_by?: string | null
  expires_at: string
  created_at: string
}

export type CoworkWorkspaceSettings = {
  workspace_name: string
  slug?: string
  onboarded?: boolean
  default_agent_id: string
  response_language?: string
  country?: string | null
  plan?: string
  seat_limit?: number
  seat_used?: number
  trial?: {
    companies_used: number
    companies_limit: number
    reports_used: number
    reports_limit: number
    projects_used: number
    projects_limit: number
    storage_used_bytes: number
    storage_limit_bytes: number
    chats_used: number
    chats_limit: number
    messages_used: number
    messages_limit: number
    ends_at: string | null
  } | null
}

export type CoworkAgentAssignment = {
  id: string
  scope_type: "workspace" | "entity" | "project"
  scope_id?: string | null
  scope_name?: string | null
}

export type CoworkWorkspaceAgent = {
  id: string
  workspace_company_id: string
  created_by_user_id: string
  name: string
  purpose: string
  instructions: string
  mode: "primary" | "subagent" | "all"
  status: "active" | "disabled"
  template_id?: string | null
  tools: Record<string, boolean>
  integrations: string[]
  permissions: Record<string, "allow" | "ask" | "deny">
  metadata: Record<string, unknown>
  assignments: CoworkAgentAssignment[]
  created_at: string
  updated_at: string
}

export type CoworkWorkspaceAgentInput = {
  name: string
  purpose?: string
  instructions?: string
  mode?: CoworkWorkspaceAgent["mode"]
  status?: CoworkWorkspaceAgent["status"]
  template_id?: string
  tools?: Record<string, boolean>
  integrations?: string[]
  permissions?: Record<string, "allow" | "ask" | "deny">
  metadata?: Record<string, unknown>
  assignments?: Array<Pick<CoworkAgentAssignment, "scope_type" | "scope_id">>
}

export type CoworkProjectSummary = CoworkProject & {
  company_name: string
  entity_count: number
  files: number
  reports: number
  workflows: number
  last_activity?: string | null
}

export type CoworkSession = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  automation_workflow_id?: string | null
  workflow_run_id?: string | null
  user_id: string
  title: string
  status: string
  remote_opencode_session_id?: string | null
  last_message_at?: string | null
  session_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkPortfolioChat = CoworkSession & {
  company_name: string
  project_name: string
  workflow_name?: string | null
  period_name?: string | null
  scope_type: "workspace" | "company" | "project"
  scope_name: string
  message_count: number
}

export type CoworkReportingWorkflowConfig = {
  template_id: string
  template_version: number
  report_type: string
  instructions?: string
  visible_prompt: string
  report_language: string
  file_ids: string[]
  period_id?: string | null
  account_card?: import("./report-templates").AccountCardSelection | null
}

export type CoworkReportRegistry = {
  schema_version: number
  domains: Array<{
    id: string
    version: string
    runtime_agent: string
    capabilities: string[]
    knowledge_types: string[]
    available: boolean
  }>
  templates: Array<{
    id: string
    version: number
    domain: string
    report_type: string
    jurisdiction: string | null
    availability: "enabled" | "disabled"
    validation_maturity: "contract" | "integrated"
    available: boolean
    definition: Record<string, unknown> | null
  }>
}

export type CoworkWorkflow = {
  id: string
  company_id: string
  project_id: string
  name: string
  purpose: string
  default_agent_id?: string | null
  status: string
  steps: string[]
  input_definitions: Array<Record<string, unknown> | string>
  output_definitions: Array<Record<string, unknown> | string>
  approval_required: boolean
  workflow_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkPortfolioWorkflow = CoworkWorkflow & {
  kind?: "automation"
  company_name: string
  project_name: string
  runs: number
  last_run_at?: string | null
  last_run_status?: string | null
  agent_name?: string | null
}

export type CoworkPeriodWorkflow = {
  kind: "period"
  id: string
  company_id: string
  project_id: string
  name: string
  company_name: string
  project_name: string
  status: string
  created_at: string
  updated_at: string
  runs: number
  agent_name?: string | null
}
export type CoworkWorkflowEntry = CoworkPortfolioWorkflow | CoworkPeriodWorkflow

export type CoworkWorkflowRunValidationFinding = {
  code: string
  classification: "blocking" | "incomplete" | "warning"
  status: "passed" | "failed"
  message: string
  evidence_refs?: string[]
  details?: Record<string, unknown>
}

export type CoworkWorkflowRunSourceOutcome = {
  source_id: string
  status: "completed" | "partial" | "unreadable" | "unsupported" | "failed"
  summary?: string | null
  evidence_refs?: string[]
  limitations?: Array<Record<string, unknown>>
  payload?: Record<string, unknown>
}

export type CoworkWorkflowRunOutputSnapshot = Record<string, unknown> & {
  validation_findings?: CoworkWorkflowRunValidationFinding[]
  source_outcomes?: CoworkWorkflowRunSourceOutcome[]
  review?: unknown
}

export type CoworkWorkflowRun = {
  id: string
  company_id: string
  project_id: string
  workflow_id: string
  period_id?: string | null
  session_id?: string | null
  session_remote_id?: string | null
  started_by_user_id: string
  agent_id?: string | null
  agent_name?: string | null
  status: "created" | "running" | "completed" | "needs_review" | "failed"
  validation_status: "passed" | "passed_with_warnings" | "failed" | "incomplete"
  approval_status: string
  started_at?: string | null
  completed_at?: string | null
  input_snapshot: Record<string, unknown>
  output_snapshot: CoworkWorkflowRunOutputSnapshot
  exception_count: number
  error_message?: string | null
  metadata: Record<string, unknown>
  progress_stage:
    | "starting"
    | "processing_files"
    | "analyzing"
    | "generating_report"
    | "waiting_for_input"
    | "ready"
    | "needs_review"
    | "failed"
    | "unknown"
  artifacts: CoworkWorkflowRunArtifact[]
  can_open_chat: boolean
  chat_unavailable_reason?: "missing_session" | "session_deleted" | "not_owner" | "scope_mismatch" | null
  validation_findings?: CoworkWorkflowRunValidationFinding[]
  source_outcomes?: CoworkWorkflowRunSourceOutcome[]
  file_extraction_statuses?: CoworkWorkflowRunSourceOutcome[]
  created_at: string
  updated_at: string
}

export type CoworkWorkflowPipelineData = {
  company_id: string
  project_id: string
  period_id?: string | null
  files: CoworkFile[]
  extractions: CoworkExtraction[]
  transformations: CoworkTransformation[]
  artifacts: CoworkArtifact[]
  approvals: CoworkApproval[]
}

export type CoworkWorkflowRunPipeline = CoworkWorkflowPipelineData & { workflow_id: string; run_id: string }
export type CoworkPeriodWorkflowPipeline = CoworkWorkflowPipelineData & { period_id: string }

export type CoworkWorkflowRunArtifact = {
  id: string
  title: string
  format: string
  report_type?: string | null
  report_status: "draft" | "ready_for_review" | "approved" | "blocked"
  validation_status: "passed" | "passed_with_warnings" | "failed" | "incomplete"
  size_bytes?: number | null
}

export type CoworkWorkflowInput = {
  name: string
  purpose?: string
  default_agent_id?: string | null
  steps?: string[]
  input_definitions?: Array<Record<string, unknown> | string>
  output_definitions?: Array<Record<string, unknown> | string>
  approval_required?: boolean
  metadata?: Record<string, unknown>
  reporting?: CoworkReportingWorkflowConfig
}

export type CoworkPeriod = {
  id: string
  company_id: string
  project_id: string
  name: string
  granularity: "year" | "quarter" | "month" | "day" | "custom"
  start_date?: string | null
  end_date?: string | null
  fiscal_year?: number | null
  period_status: "open" | "processing" | "needs_review" | "closing" | "closed" | "locked"
  is_current: boolean
  is_locked: boolean
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkMessage = {
  id: string
  project_id: string
  session_id: string
  role: "user" | "assistant" | "system" | "tool"
  content: string
  structured_payload: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkActivityLog = {
  id: string
  kind: "audit" | "message"
  event_type: string
  title: string
  description?: string | null
  project_id?: string | null
  project_name?: string | null
  workflow_id?: string | null
  workflow_name?: string | null
  session_id?: string | null
  session_title?: string | null
  user_id?: string | null
  user_email?: string | null
  user_name?: string | null
  payload: Record<string, unknown>
  details: Record<string, string>
  created_at: string
}

export type CoworkUsageStats = {
  projects: number
  workflows: number
  chats: number
  sessions: number
  agents: number
  files: number
  extractions: number
  transformations: number
  output_tables: number
  reports: number
  approvals: number
  input_tokens: number
  output_tokens: number
  total_tokens: number
  estimated_cost: number
  last_updated_at: string
  by_project: Array<Record<string, unknown>>
  by_workflow: Array<Record<string, unknown>>
  recent_runs: Array<Record<string, unknown>>
  recent_activity: Array<Record<string, unknown>>
  file_stats: Record<string, unknown>
  efficiency: Record<string, unknown>
  data_sources: Array<Record<string, unknown>>
}

export type CoworkAccountingSummary = {
  project_id: string
  accounting_enabled: boolean
  accounting_agent_enabled: boolean
  summary: Record<string, number>
  tabs: string[]
}

export type CoworkAccountingReadiness = {
  workflow_id: string
  ready_to_close: boolean
  checks: Array<{ key: string; passed: boolean; current: number; total: number }>
  warnings: Array<{ key: string; count: number }>
  next_actions: Array<{ key: string; tab: string }>
  summary: Record<string, number>
}

export type CoworkAccountingContextUpdate = {
  source_basis?: "document_facts" | "live_operational" | "posted_books" | "reference_knowledge"
  active_view?: "files" | "billing" | "books" | "reports" | "other"
  selected_tab?: string
  selected_review_id?: string
  selected_document_id?: string
  selected_posting_group_id?: string
  selected_payment_match_id?: string
  source_filename?: string
  filters?: Record<string, unknown>
}

export type CoworkAccountingDocument = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  accounting_period_id?: string | null
  source_file_id?: string | null
  source_filename?: string | null
  document_type?: string | null
  document_number?: string | null
  document_date?: string | null
  due_date?: string | null
  currency?: string | null
  payment_method?: string | null
  counterparty_id?: string | null
  counterparty_name?: string | null
  counterparty_country?: string | null
  counterparty_vat_number?: string | null
  net_amount?: number | null
  vat_amount?: number | null
  rounding_amount?: number | null
  gross_amount?: number | null
  vat_rate?: number | null
  extraction_confidence?: number | null
  classification_confidence?: number | null
  vat_confidence?: number | null
  payment_match_confidence?: number | null
  processing_status: string
  review_status: string
  duplicate_status: string
  document_metadata: Record<string, unknown>
  posting_group_id?: string | null
  posting_status?: string | null
  posting_validation_status?: string | null
  posting_confidence?: number | null
  created_at: string
  updated_at: string
}

export type CoworkSuggestedPostingLine = {
  id: string
  posting_group_id: string
  debit_account?: string | null
  credit_account?: string | null
  amount: number
  currency?: string | null
  description?: string | null
  analytics_json: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkSuggestedPostingGroup = {
  id: string
  company_id: string
  document_id: string
  status: string
  confidence?: number | null
  validation_status: string
  validation_errors: string[]
  explanation?: string | null
  lines: CoworkSuggestedPostingLine[]
  created_at: string
  updated_at: string
}

export type CoworkAccountingReview = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  document_id: string
  posting_group_id?: string | null
  review_status: string
  reviewed_by_user_id?: string | null
  reviewed_at?: string | null
  changes_json: Record<string, unknown>
  comment?: string | null
  document?: CoworkAccountingDocument | null
  posting_group?: CoworkSuggestedPostingGroup | null
  created_at: string
  updated_at: string
}

export type CoworkPaymentTransaction = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  accounting_period_id?: string | null
  bank_account_id?: string | null
  external_id?: string | null
  transaction_date?: string | null
  counterparty_name?: string | null
  counterparty_iban?: string | null
  description?: string | null
  reference?: string | null
  amount: number
  currency?: string | null
  status: string
  transaction_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkPaymentMatch = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  document_id: string
  payment_id: string
  matched_amount: number
  currency?: string | null
  confidence?: number | null
  match_reasons: string[]
  status: string
  reviewed_by_user_id?: string | null
  reviewed_at?: string | null
  document?: CoworkAccountingDocument | null
  payment?: CoworkPaymentTransaction | null
  created_at: string
  updated_at: string
}

export type CoworkAccountingExportFile = {
  id: string
  company_id: string
  package_id: string
  file_type: string
  filename: string
  content_type: string
  checksum_sha256: string
  size: number
  file_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkAccountingExportFileDownload = CoworkAccountingExportFile & {
  content: string
}

export type CoworkAccountingExportPackage = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  review_id?: string | null
  package_type: string
  status: string
  idempotency_key: string
  checksum_sha256: string
  validation_status: string
  validation_errors: string[]
  manual_submission_status: string
  export_metadata: Record<string, unknown>
  files: CoworkAccountingExportFile[]
  created_at: string
  updated_at: string
}

export type CoworkLedgerSummary = {
  workflow_id: string
  workflow_name?: string | null
  journal_entries: number
  posting_lines: number
  debit_total: number
  credit_total: number
  imbalance: number
  balanced: boolean
  status_counts: Record<string, number>
}

export type CoworkLedgerJournalLine = {
  id: string
  posting_group_id: string
  document_id?: string | null
  payment_id?: string | null
  date?: string | null
  document_reference?: string | null
  source_filename?: string | null
  counterparty_name?: string | null
  description?: string | null
  debit_account?: string | null
  debit_account_name?: string | null
  credit_account?: string | null
  credit_account_name?: string | null
  amount: number
  currency?: string | null
  status: string
  analytics_json: Record<string, unknown>
  confidence?: number | null
  explanation?: string | null
  approved_by_user_id?: string | null
  approved_at?: string | null
}

export type CoworkLedgerTrialBalanceRow = {
  account: string
  account_name?: string | null
  opening_debit?: number | null
  opening_credit?: number | null
  period_debit: number
  period_credit: number
  closing_debit?: number | null
  closing_credit?: number | null
  opening_available: boolean
}

export type CoworkLedgerView = {
  summary: CoworkLedgerSummary
  journal: CoworkLedgerJournalLine[]
  trial_balance: CoworkLedgerTrialBalanceRow[]
}

export type CoworkAccountingTaxSource = {
  id: string
  title: string
  url: string
  source_type: string
  jurisdiction: string
  report_type: string
}

export type CoworkAccountingTaxReport = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  report_type: string
  jurisdiction: string
  reporting_period?: string | null
  status: string
  validation_status: string
  validation_errors: Array<Record<string, unknown> | string>
  approval_status: string
  prepared_by_user_id?: string | null
  approved_by_user_id?: string | null
  approved_at?: string | null
  source_citations_json: CoworkAccountingTaxSource[]
  report_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkFile = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  period_id?: string | null
  filename: string
  content_type: string
  size: number
  status: string
  parsed_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkFilePart = {
  id: string
  file_id: string
  name: string
  part_type: string
  index: number
  preview_text?: string | null
  part_metadata: Record<string, unknown>
}

export type CoworkFileDetail = CoworkFile & {
  parts: CoworkFilePart[]
}

export type CoworkPortfolioFile = CoworkFile & {
  session_id?: string | null
  company_name: string
  project_name: string
  period_name?: string | null
  source: string
  document_summary?: CoworkFileDocumentSummary
  import_status?: string | null
}

export type CoworkFileDocumentSummary = {
  extraction_state: "not_extracted" | "extracted" | "partial" | "failed"
  document_type: string
  sellers: { id: string; name: string; vat_number?: string | null; registration_number?: string | null }[]
  document_numbers: string[]
  extraction_id?: string | null
}

export type CoworkFileCatalogFacet = { id: string; label: string; count: number; vat_number?: string | null }
export type CoworkFileCatalog = {
  items: CoworkPortfolioFile[]
  total: number
  available_file_ids: string[]
  sellers: CoworkFileCatalogFacet[]
  document_types: CoworkFileCatalogFacet[]
  accounting: boolean
}

export type CoworkKnowledge = {
  knowledge_id: string
  company_id: string
  project_id: string | null
  file_id: string
  status: string
  filename: string
  content_type: string
  size: number
  parsed_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type CoworkSessionContext = {
  company_id: string
  project_id: string
  project_name?: string | null
  workflow_id?: string | null
  workflow_name?: string | null
  period_id?: string | null
  period_name?: string | null
  period_granularity?: CoworkPeriod["granularity"] | null
  period_start_date?: string | null
  period_end_date?: string | null
  period_status?: CoworkPeriod["period_status"] | null
  period_is_locked?: boolean
  session_id: string
  remote_opencode_session_id: string
  title: string
  session_metadata: Record<string, unknown>
  accounting_profiles: Array<{
    entity_id: string
    entity_name: string
    jurisdiction?: string | null
    reporting_framework?: string | null
    business_activity?: Record<string, unknown>
    direct_cost_rules?: string[]
    operating_expense_rules?: string[]
    selling_expense_rules?: string[]
    capitalization_rules?: Array<Record<string, unknown> | string>
    account_mappings?: Record<string, unknown>
    expense_taxonomy?: Record<string, unknown>
    fixed_asset_taxonomy?: Record<string, unknown>
    pnl_taxonomy?: Record<string, unknown>
    materiality?: Record<string, unknown>
    metadata?: Record<string, unknown>
  }>
}

export type CoworkProjectMember = {
  id: string
  company_id: string
  project_id: string
  user_id: string
  user_email?: string | null
  user_name?: string | null
  role: "viewer" | "editor" | "owner"
  status: string
  invited_by_user_id?: string | null
  created_at: string
  updated_at: string
}

export type CoworkArtifact = {
  id: string
  company_id: string
  project_id: string
  workflow_id?: string | null
  session_id?: string | null
  workflow_run_id?: string | null
  artifact_type: string
  report_type?: string | null
  format: string
  title: string
  preview_html?: string | null
  artifact_metadata: Record<string, unknown>
  report_status: "draft" | "ready_for_review" | "approved" | "blocked"
  validation_status: "passed" | "passed_with_warnings" | "failed" | "incomplete"
  entity_scope: { type?: string; entity_ids?: string[]; [key: string]: unknown }
  period_start?: string | null
  period_end?: string | null
  comparison_period?: Record<string, unknown> | null
  reporting_currency?: string | null
  source_coverage: Record<string, unknown>
  summary: Record<string, unknown>
  exceptions: Array<Record<string, unknown>>
  validation_results: Array<Record<string, unknown>>
  lineage: Array<Record<string, unknown>>
  mapping_coverage?: number | null
  mapping_coverage_details: Record<string, unknown>
  status: string
  created_at: string
  updated_at: string
}

export type CoworkPortfolioReport = CoworkArtifact & {
  company_name: string
  project_name: string
  workflow_name?: string | null
  period_name?: string | null
}

export type CoworkExtraction = {
  field_view?: {
    contract_version: string
    data: Record<string, unknown>
    labels: Record<string, string>
    has_source_fields?: boolean
  } | null
  id: string
  project_id: string
  workflow_id?: string | null
  file_id?: string | null
  workflow_run_id?: string | null
  schema_name?: string | null
  prompt?: string | null
  result_json: Record<string, unknown>
  confidence?: number | null
  status: string
  created_at: string
  updated_at: string
}

export type CoworkTransformation = {
  id: string
  project_id: string
  workflow_id?: string | null
  workflow_run_id?: string | null
  instructions: string
  source_extraction_ids: string[]
  steps_json: Array<Record<string, unknown>>
  output_json: Record<string, unknown>
  status: string
  created_at: string
  updated_at: string
}

export type CoworkFileExtraction = CoworkExtraction & {
  company_id: string
  period_id?: string | null
  record_type: "workflow" | "source" | "report"
  summary?: string | null
  revision?: number | null
  schema_version?: string | null
  extractor_key?: string | null
  extractor_version?: string | null
  quality_status?: string | null
  limitations: Record<string, unknown>[]
  provenance: Record<string, unknown>
  superseded: boolean
  current_content?: boolean | null
}

export type CoworkReportShare = {
  id: string
  report_artifact_id: string
  shared_by_user_id: string
  recipient_email: string
  message?: string | null
  status: "pending" | "sent" | "failed"
  sent_at?: string | null
  created_at: string
}

export type CoworkSharedReport = {
  report_title: string
  report_format: string
  report_filename: string
  message?: string | null
  shared_by: string
  shared_at: string
}

export type CoworkApproval = {
  id: string
  company_id: string
  project_id: string
  report_artifact_id: string
  requested_by_user_id: string
  approver_email: string
  status: string
  comments?: string | null
  created_at: string
  updated_at: string
}

export type CoworkPortfolioApproval = CoworkApproval & {
  company_name: string
  project_name: string
  report_title: string
  report_format: string
  report_status: string
  period_name?: string | null
}

export type CoworkApprovalComment = {
  id: string
  kind: "request" | "comment" | "approved" | "rejected" | "legacy"
  author_name?: string | null
  author_email?: string | null
  body: string
  created_at: string
}

export type CoworkApprovalReview = {
  approval_id: string
  approver_email: string
  requested_by_user_id: string
  requested_by_email?: string | null
  requested_by_name?: string | null
  status: string
  comments?: string | null
  comment_history?: CoworkApprovalComment[]
  report_title: string
  report_format: string
  report_content_type: string
  report_filename: string
  report_content?: string | null
  created_at: string
  updated_at: string
}

export type CoworkApprovalDetail = CoworkApprovalReview & {
  company_id: string
  project_id: string
  report_artifact_id: string
  can_comment: boolean
}

export type CoworkConnectSession = {
  token: string
  connect_link: string
  expires_at: string
}

export type CoworkIntegrationConnection = {
  connection_id: string
  provider_config_key: string
  provider_key?: string | null
  provider?: string | null
  end_user?: Record<string, unknown> | null
  metadata?: Record<string, unknown> | null
  created_at?: string | null
  updated_at?: string | null
}

export type CoworkCloudFile = {
  id: string
  name: string
  mime_type?: string | null
  size?: number | null
  is_folder: boolean
  drive_id?: string | null
  parent_id?: string | null
  path?: string | null
  version?: string | null
  modified_at?: string | null
  raw?: Record<string, unknown> | null
}

export type CoworkStreamEvent =
  | { type: "user_message"; message_id: string }
  | { type: "assistant_message"; message_id: string }
  | { type: "token"; content: string }
  | { type: "run_progress"; steps?: Array<{ label: string; status: string }> }
  | { type: "tool_start"; tool: string; message: string }
  | { type: "final_answer"; content: string }
  | { type: "error"; error: string }

let tokenProvider: ((forceRefresh?: boolean) => Promise<string>) | undefined

export class CoworkApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = "CoworkApiError"
  }
}

export function setCoworkAuthTokenProvider(provider: (forceRefresh?: boolean) => Promise<string>) {
  tokenProvider = provider
}

const jsonHeaders = async (companyID?: string) => ({
  ...(await authHeaders(false, companyID)),
  "Content-Type": "application/json",
})

export function coworkApiBaseUrl() {
  return (import.meta.env.VITE_AGENT_API_URL || "http://localhost:8088").replace(/\/$/, "")
}

export async function coworkAuthHeaders(companyID?: string) {
  return authHeaders(false, companyID)
}

export function coworkAgentMode() {
  return import.meta.env.VITE_AGENT_MODE === "true"
}

export function coworkVersion() {
  return "1.0.0"
}

export type CoworkRuntimeConnection = {
  url: string
  username: string
  password: string
  displayName?: string
}

export async function getRuntimeConnection(companyID?: string) {
  return requestJson<CoworkRuntimeConnection>("/api/runtime/connection", undefined, companyID)
}

export async function listProjects(companyID?: string) {
  return boundedRead<CoworkProject[]>("/api/projects", companyID)
}

export async function getProject(projectID: string, companyID: string) {
  return boundedRead<CoworkProject>(`/api/projects/${encodeURIComponent(projectID)}`, companyID)
}

export async function listCompanies() {
  return requestJson<CoworkCompany[]>("/api/companies")
}

export async function listCompanySummaries() {
  return requestJson<CoworkCompanySummary[]>("/api/companies/summary")
}

export type CoworkCompanyRegistrySuggestion = {
  name: string
  registration_number: string
  country: "EE"
  address?: string | null
  source_url: string
}

export type CoworkCompanyRegistryDetails = Omit<CoworkCompanyRegistrySuggestion, "address"> & {
  vat_number: string | null
}

export async function searchCompanyRegistry(query: string, signal?: AbortSignal) {
  const params = new URLSearchParams({ q: query.trim() })
  return requestJson<CoworkCompanyRegistrySuggestion[]>(`/api/company-registry/search?${params}`, { signal })
}

export async function getCompanyRegistryDetails(registrationNumber: string, signal?: AbortSignal) {
  return requestJson<CoworkCompanyRegistryDetails>(`/api/company-registry/${encodeURIComponent(registrationNumber)}`, {
    signal,
  })
}

export async function createCompany(input: {
  name: string
  country?: string
  registration_number?: string
  vat_number?: string | null
}) {
  return requestJson<CoworkCompanySummary>("/api/companies", {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export async function updateCompany(
  companyID: string,
  input: { name?: string; country?: string; registration_number?: string; vat_number?: string | null; status?: string },
) {
  return requestJson<CoworkCompanySummary>(`/api/companies/${companyID}`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export type CoworkCompanyEmployee = {
  id: string
  company_id: string
  first_name: string
  last_name: string
  personal_id: string
  created_at: string
  updated_at: string
}

export type CoworkCompanyEmployeeInput = Pick<CoworkCompanyEmployee, "first_name" | "last_name" | "personal_id">

export async function listCompanyEmployees(companyID: string, signal?: AbortSignal) {
  return requestJson<CoworkCompanyEmployee[]>(
    `/api/companies/${encodeURIComponent(companyID)}/employees`,
    { signal, cache: "no-store" },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function createCompanyEmployee(companyID: string, input: CoworkCompanyEmployeeInput) {
  return requestJson<CoworkCompanyEmployee>(
    `/api/companies/${encodeURIComponent(companyID)}/employees`,
    { method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input) },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function updateCompanyEmployee(companyID: string, employeeID: string, input: CoworkCompanyEmployeeInput) {
  return requestJson<CoworkCompanyEmployee>(
    `/api/companies/${encodeURIComponent(companyID)}/employees/${encodeURIComponent(employeeID)}`,
    { method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input) },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function deleteCompanyEmployee(companyID: string, employeeID: string) {
  return requestJson<void>(
    `/api/companies/${encodeURIComponent(companyID)}/employees/${encodeURIComponent(employeeID)}`,
    { method: "DELETE" },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function getCompanyAccountingProfile(companyID: string) {
  return requestJson<CoworkCompanyAccountingProfile>(
    `/api/companies/${encodeURIComponent(companyID)}/accounting-profile`,
    undefined,
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export type CoworkCompanyAccountingProfileInput = Pick<
  CoworkCompanyAccountingProfile,
  "jurisdiction" | "base_currency" | "fiscal_year_start" | "fiscal_year_end" | "vat_number" | "vat_reporting_frequency"
>

export async function updateCompanyAccountingProfile(companyID: string, input: CoworkCompanyAccountingProfileInput) {
  return requestJson<CoworkCompanyAccountingProfile>(
    `/api/companies/${encodeURIComponent(companyID)}/accounting-profile`,
    { method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input) },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function deleteCompany(companyID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/companies/${companyID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listWorkspaceMembers() {
  return requestJson<CoworkWorkspaceMember[]>("/api/workspace/members")
}

export async function listWorkspaceInvitations() {
  return requestJson<CoworkWorkspaceInvitation[]>("/api/workspace/invitations")
}

export async function createWorkspaceInvitation(email: string, role: CoworkWorkspaceInvitation["role"]) {
  return requestJson<CoworkWorkspaceInvitation>("/api/workspace/invitations", {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ email, role }),
  })
}

export async function resendWorkspaceInvitation(invitationID: string) {
  return requestJson<CoworkWorkspaceInvitation>(
    `/api/workspace/invitations/${encodeURIComponent(invitationID)}/resend`,
    {
      method: "POST",
      headers: await authHeaders(),
    },
  )
}

export async function revokeWorkspaceInvitation(invitationID: string) {
  return requestJson<void>(`/api/workspace/invitations/${encodeURIComponent(invitationID)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
}

export async function removeWorkspaceMember(userID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/workspace/members/${encodeURIComponent(userID)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export type CoworkMe = {
  user_id: string
  email?: string | null
  company_id: string
  company_role: string
  platform_admin: boolean
}

export type CoworkAccessRequest = {
  id: string
  email: string
  locale: string
  firm_name?: string | null
  status: "requested" | "approved" | "declined"
  company_id?: string | null
  created_at: string
  approved_at?: string | null
}

export async function getMe() {
  return requestJson<CoworkMe>("/api/me")
}

export async function listAccessRequests(status: "requested" | "all" = "requested") {
  return requestJson<CoworkAccessRequest[]>(`/api/admin/access-requests?status=${status}`, undefined, undefined, {
    preserveAuthOnForbidden: true,
  })
}

export async function approveAccessRequest(requestID: string, workspaceName?: string) {
  return requestJson<CoworkAccessRequest>(
    `/api/admin/access-requests/${encodeURIComponent(requestID)}/approve`,
    { method: "POST", headers: await jsonHeaders(), body: JSON.stringify({ workspace_name: workspaceName || null }) },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export async function declineAccessRequest(requestID: string) {
  return requestJson<CoworkAccessRequest>(
    `/api/admin/access-requests/${encodeURIComponent(requestID)}/decline`,
    { method: "POST", headers: await authHeaders() },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export type CoworkAdminUser = {
  id: string
  email: string | null
  full_name: string | null
  status: "active" | "disabled" | "deleted"
  has_password: boolean
  workspaces: string[]
  created_at: string
}

export async function listAdminUsers(search = "", offset = 0) {
  const query = new URLSearchParams({ search, offset: String(offset) })
  return requestJson<CoworkAdminUser[]>(`/api/admin/users?${query}`, undefined, undefined, {
    preserveAuthOnForbidden: true,
  })
}

export async function createAdminUser(input: { email: string; full_name: string; workspace_name: string }) {
  return requestJson<CoworkAdminUser>(
    "/api/admin/users",
    { method: "POST", headers: await jsonHeaders(), body: JSON.stringify(input) },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export async function updateAdminUser(userID: string, input: { full_name?: string; status?: "active" | "disabled" }) {
  return requestJson<CoworkAdminUser>(
    `/api/admin/users/${encodeURIComponent(userID)}`,
    { method: "PATCH", headers: await jsonHeaders(), body: JSON.stringify(input) },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export async function deleteAdminUser(userID: string) {
  return requestJson<void>(
    `/api/admin/users/${encodeURIComponent(userID)}`,
    { method: "DELETE", headers: await authHeaders() },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export type CoworkWorkspace = {
  id: string
  name: string
  slug: string
  role: "viewer" | "member" | "admin" | "owner"
  onboarded: boolean
  company_ids: string[]
}

export type CoworkHandleAvailability = {
  value: string
  available: boolean
  reason?: "invalid" | "reserved" | "taken" | null
  suggestion?: string | null
}

export type CoworkWorkspaceProfile = {
  user_id: string
  email?: string | null
  full_name?: string | null
  username: string
}

export async function listWorkspaces() {
  return requestJson<CoworkWorkspace[]>("/api/workspaces")
}

export async function checkWorkspaceSlug(slug: string, signal?: AbortSignal) {
  const params = new URLSearchParams({ slug })
  return requestJson<CoworkHandleAvailability>(`/api/workspaces/slug-availability?${params}`, { signal })
}

export async function checkUsername(username: string, signal?: AbortSignal) {
  const params = new URLSearchParams({ username })
  return requestJson<CoworkHandleAvailability>(`/api/workspace/username-availability?${params}`, { signal })
}

export async function getWorkspaceProfile() {
  return requestJson<CoworkWorkspaceProfile>("/api/workspace/profile")
}

export async function updateWorkspaceProfile(input: { full_name?: string; username?: string }) {
  return requestJson<CoworkWorkspaceProfile>("/api/workspace/profile", {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export type CoworkWorkspacePlan = "internal" | "early_access" | "trial" | "platform" | "enterprise"

export type CoworkAdminWorkspace = {
  id: string
  name: string
  slug: string
  plan: CoworkWorkspacePlan
  seat_limit: number
  seats_used: number
  companies: number
  owner_email?: string | null
  billing_email?: string | null
  created_at: string
  month: string
  active_companies: number
  report_runs: number
  estimated_invoice_eur: number
  fair_use_companies: string[]
}

export async function listAdminWorkspaces(month?: string) {
  const query = month ? `?${new URLSearchParams({ month })}` : ""
  return requestJson<CoworkAdminWorkspace[]>(`/api/admin/workspaces${query}`, undefined, undefined, {
    preserveAuthOnForbidden: true,
  })
}

export async function updateAdminWorkspace(
  workspaceID: string,
  input: { plan?: CoworkWorkspacePlan; seat_limit?: number },
) {
  return requestJson<CoworkAdminWorkspace>(
    `/api/admin/workspaces/${encodeURIComponent(workspaceID)}`,
    { method: "PATCH", headers: await jsonHeaders(), body: JSON.stringify(input) },
    undefined,
    { preserveAuthOnForbidden: true },
  )
}

export async function completeOnboarding() {
  return requestJson<CoworkWorkspaceSettings>("/api/workspace/onboarding/complete", {
    method: "POST",
    headers: await authHeaders(),
  })
}

export async function saveOnboardingPreferences(input: { country: string; response_language: string }) {
  return requestJson<{ company_id: string; project_id: string; country: string; response_language: string }>(
    "/api/workspace/onboarding/preferences",
    { method: "POST", headers: await jsonHeaders(), body: JSON.stringify(input) },
  )
}

export async function getWorkspaceSettings() {
  return getWorkspaceSettingsForCompany()
}

export async function getWorkspaceSettingsForCompany(companyID?: string) {
  return requestJson<CoworkWorkspaceSettings>("/api/workspace/settings", undefined, companyID)
}

export async function updateWorkspaceSettings(input: Partial<CoworkWorkspaceSettings>) {
  return requestJson<CoworkWorkspaceSettings>("/api/workspace/settings", {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export async function listWorkspaceAgents() {
  return listWorkspaceAgentsForCompany()
}

export async function listWorkspaceAgentsForCompany(companyID?: string) {
  return requestJson<CoworkWorkspaceAgent[]>("/api/workspace/agents", undefined, companyID)
}

export async function createWorkspaceAgent(input: CoworkWorkspaceAgentInput) {
  return requestJson<CoworkWorkspaceAgent>("/api/workspace/agents", {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export async function updateWorkspaceAgent(agentID: string, input: Partial<CoworkWorkspaceAgentInput>) {
  return requestJson<CoworkWorkspaceAgent>(`/api/workspace/agents/${agentID}`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(input),
  })
}

export async function updateWorkspaceAgentAssignments(
  agentID: string,
  assignments: Array<Pick<CoworkAgentAssignment, "scope_type" | "scope_id">>,
) {
  return requestJson<CoworkWorkspaceAgent>(`/api/workspace/agents/${agentID}/assignments`, {
    method: "PUT",
    headers: await jsonHeaders(),
    body: JSON.stringify(assignments),
  })
}

export async function updateWorkspaceAgentFocus(agentID: string, preferredSkills: string[], instructions: string) {
  return requestJson<CoworkWorkspaceAgent>(`/api/workspace/agents/${agentID}/focus`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify({ preferred_skills: preferredSkills, instructions }),
  })
}

export async function deleteWorkspaceAgent(agentID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/workspace/agents/${agentID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listProjectSummaries() {
  return boundedRead<CoworkProjectSummary[]>("/api/projects/summary")
}

export async function listActivityLogs(options?: {
  kind?: string
  search?: string
  limit?: number
  projectID?: string
  periodID?: string
  beforeID?: string
  companyID?: string
}) {
  const params = new URLSearchParams()
  if (options?.kind) params.set("kind", options.kind)
  if (options?.search) params.set("search", options.search)
  if (options?.limit) params.set("limit", String(options.limit))
  if (options?.projectID) params.set("project_id", options.projectID)
  if (options?.periodID && options.periodID !== "ALL") params.set("period_id", options.periodID)
  if (options?.beforeID) params.set("before_id", options.beforeID)
  return requestJson<CoworkActivityLog[]>(
    `/api/activity${params.size ? `?${params.toString()}` : ""}`,
    undefined,
    options?.companyID,
  )
}

export async function getUsageStats() {
  return requestJson<CoworkUsageStats>("/api/usage/stats")
}

export async function getAccountingProjectSummary(projectID: string, workflowID?: string | null) {
  const params = workflowID ? `?workflow_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkAccountingSummary>(`/api/v1/accounting/projects/${projectID}${params}`)
}

export async function getAccountingReadiness(workflowID: string) {
  return requestJson<CoworkAccountingReadiness>(`/api/v1/accounting/workflows/${workflowID}/readiness`).catch(
    () => undefined,
  )
}

export async function listAccountingDocuments(workflowID: string) {
  return requestJson<CoworkAccountingDocument[]>(`/api/v1/accounting/workflows/${workflowID}/documents`).catch(() => [])
}

export async function createAccountingDocument(workflowID: string, sourceFileID: string) {
  return requestJson<CoworkAccountingDocument>(`/api/v1/accounting/workflows/${workflowID}/documents`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ source_file_id: sourceFileID }),
  })
}

export async function processAccountingDocument(documentID: string) {
  return requestJson<CoworkAccountingDocument>(`/api/v1/accounting/documents/${documentID}/process`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export async function generateAccountingPostingSuggestion(documentID: string) {
  return requestJson<CoworkSuggestedPostingGroup>(`/api/v1/accounting/documents/${documentID}/posting-suggestions`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export async function listAccountingReviews(workflowID: string) {
  return requestJson<CoworkAccountingReview[]>(`/api/v1/accounting/workflows/${workflowID}/reviews`).catch(() => [])
}

export async function approveAccountingReview(reviewID: string, comment?: string) {
  return requestJson<CoworkAccountingReview>(`/api/v1/accounting/reviews/${reviewID}/approve`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ comment }),
  })
}

export async function rejectAccountingReview(reviewID: string, comment?: string) {
  return requestJson<CoworkAccountingReview>(`/api/v1/accounting/reviews/${reviewID}/reject`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ comment }),
  })
}

export async function listPaymentTransactions(workflowID: string) {
  return requestJson<CoworkPaymentTransaction[]>(`/api/v1/accounting/workflows/${workflowID}/payments`).catch(() => [])
}

export async function importPaymentTransactions(
  workflowID: string,
  transactions: Array<Partial<CoworkPaymentTransaction> & { amount: number }>,
) {
  return requestJson<CoworkPaymentTransaction[]>(`/api/v1/accounting/workflows/${workflowID}/payments/import`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ transactions }),
  })
}

export async function listPaymentMatches(workflowID: string) {
  return requestJson<CoworkPaymentMatch[]>(`/api/v1/accounting/workflows/${workflowID}/payment-matches`).catch(() => [])
}

export async function generatePaymentMatches(workflowID: string) {
  return requestJson<CoworkPaymentMatch[]>(`/api/v1/accounting/workflows/${workflowID}/payment-matches`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export async function approvePaymentMatch(matchID: string) {
  return requestJson<CoworkPaymentMatch>(`/api/v1/accounting/payment-matches/${matchID}/approve`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({}),
  })
}

export async function rejectPaymentMatch(matchID: string) {
  return requestJson<CoworkPaymentMatch>(`/api/v1/accounting/payment-matches/${matchID}/reject`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({}),
  })
}

export async function listAccountingExportPackages(workflowID: string) {
  return requestJson<CoworkAccountingExportPackage[]>(
    `/api/v1/accounting/workflows/${workflowID}/export-packages`,
  ).catch(() => [])
}

export async function getAccountingLedgerView(workflowID: string) {
  return requestJson<CoworkLedgerView>(`/api/v1/accounting/workflows/${workflowID}/ledger`)
}

export type BookPosting = { account_code: string; dimension_id?: string | null; debit: string; credit: string }
export type BookJournal = {
  is_storno?: boolean
  revision_number?: number; correction_of_journal_id?: string | null; reversed_by_journal_id?: string | null
  corrected_journal_id?: string | null; can_edit_posted?: boolean; can_void_posted?: boolean; can_return_to_draft?: boolean; can_create_correction?: boolean
  posting_token?: string | null
  posted_edit_block_reason?: string | null
  id: string
  period_id: string
  entry_date: string
  description: string
  document_number: string | null
  entry_type: "manual" | "opening" | "reversal" | "invoice" | "payment" | "allocation" | "allocation_reversal" | "credit" | "refund" | "payroll" | "wage_payment" | "advance_receipt" | "advance_application" | "supplier_advance" | "supplier_advance_application" | "advance_refund" | "advance_application_reversal" | "supplier_advance_application_reversal"
  status: "draft" | "posted" | "voided" | "deleted"
  reversed_journal_id: string | null
  lines: BookPosting[]
  source_review_note?: string | null
  source_period_start?: string | null; source_period_end?: string | null
  unresolved_account_codes?: string[]
  date_outside_period?: boolean
}
export type BookAccount = { code: string; name: string; account_type: "asset" | "liability" | "equity" | "revenue" | "expense"; status: "active" | "inactive" }
export type BookBalance = {
  account_code: string
  account_name: string
  opening_debit: string
  opening_credit: string
  period_debit: string
  period_credit: string
  closing_debit: string
  closing_credit: string
}
const bookPath = (projectID: string) => `/api/v1/accounting/books/projects/${encodeURIComponent(projectID)}`
const periodBookPath = (projectID: string, periodID: string) => `${bookPath(projectID)}/periods/${encodeURIComponent(periodID)}`

export async function listBookAccounts(projectID: string, companyID: string) {
  return boundedRead<BookAccount[]>(`${bookPath(projectID)}/accounts`, companyID)
}
export async function addBookAccount(projectID: string, companyID: string, input: Omit<BookAccount, "status">) {
  return requestJson<BookAccount>(`${bookPath(projectID)}/accounts`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function setBookAccountStatus(projectID: string, companyID: string, code: string, status: "active" | "inactive") {
  return requestJson<BookAccount>(`${bookPath(projectID)}/accounts/${encodeURIComponent(code)}/status`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify({ status }),
  }, companyID)
}
export async function updateBookAccount(projectID: string, companyID: string, code: string, input: Omit<BookAccount, "status">) {
  return requestJson<BookAccount>(`${bookPath(projectID)}/accounts/${encodeURIComponent(code)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function deleteBookAccount(projectID: string, companyID: string, code: string) {
  return requestJson<void>(`${bookPath(projectID)}/accounts/${encodeURIComponent(code)}`, {
    method: "DELETE", headers: await jsonHeaders(companyID),
  }, companyID)
}
export type BookMoneyAccount = { account_code: string; kind: "bank" | "cash" }
export async function listBookMoneyAccounts(projectID: string, companyID: string) {
  return boundedRead<BookMoneyAccount[]>(`${bookPath(projectID)}/money-accounts`, companyID)
}
export async function registerBookMoneyAccount(projectID: string, companyID: string, accountCode: string, kind: BookMoneyAccount["kind"]) {
  return requestJson<BookMoneyAccount>(`${bookPath(projectID)}/money-accounts`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ account_code: accountCode, kind }),
  }, companyID)
}
export async function deleteBookMoneyAccount(projectID: string, companyID: string, code: string) {
  return requestJson<void>(`${bookPath(projectID)}/money-accounts/${encodeURIComponent(code)}`, {
    method: "DELETE", headers: await jsonHeaders(companyID),
  }, companyID)
}
export type BookProduct = {
  id: string; code: string; name: string; name_en: string | null
  product_type: "product" | "service"; description: string | null; unit: string | null
  sale_price: string | null; purchase_cost: string | null; vat_rate: string | null
  income_account_code: string | null; purchase_account_code: string | null; default_dimension_id: string | null
  emtak_code: string | null; status: "active" | "inactive"
}
export type BookProductInput = {
  code: string; name: string; name_en?: string; product_type: "product" | "service"
  description?: string; unit?: string; sale_price?: string; purchase_cost?: string
  vat_rate?: string; income_account_code?: string; purchase_account_code?: string;
  default_dimension_id?: string; emtak_code?: string
}
export async function listBookProducts(projectID: string, companyID: string) {
  return boundedRead<BookProduct[]>(`${bookPath(projectID)}/products`, companyID)
}
export async function createBookProduct(projectID: string, companyID: string, input: BookProductInput) {
  return requestJson<BookProduct>(`${bookPath(projectID)}/products`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookProduct(projectID: string, companyID: string, productID: string, input: BookProductInput) {
  return requestJson<BookProduct>(`${bookPath(projectID)}/products/${encodeURIComponent(productID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function setBookProductStatus(projectID: string, companyID: string, productID: string, status: "active" | "inactive") {
  return requestJson<BookProduct>(`${bookPath(projectID)}/products/${encodeURIComponent(productID)}/status`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify({ status }),
  }, companyID)
}
export type BookDimension = {
  id: string; code: string; name: string; center_type: "project" | "location" | "person"
  parent_id: string | null; description: string | null; status: "active" | "inactive"
}
export async function listBookDimensions(projectID: string, companyID: string) {
  return boundedRead<BookDimension[]>(`${bookPath(projectID)}/dimensions`, companyID)
}
export async function createBookDimension(projectID: string, companyID: string,
  input: { code: string; name: string; center_type: BookDimension["center_type"]; parent_id?: string; description?: string }) {
  return requestJson<BookDimension>(`${bookPath(projectID)}/dimensions`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookDimension(projectID: string, companyID: string, dimensionID: string,
  input: { name: string; description?: string }) {
  return requestJson<BookDimension>(`${bookPath(projectID)}/dimensions/${encodeURIComponent(dimensionID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function setBookDimensionStatus(projectID: string, companyID: string, dimensionID: string,
  status: "active" | "inactive") {
  return requestJson<BookDimension>(`${bookPath(projectID)}/dimensions/${encodeURIComponent(dimensionID)}/status`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify({ status }),
  }, companyID)
}
export type BookInvoiceSeries = {
  id: string; kind: "sale" | "prepayment"; prefix: string; first_number: number; next_number: number
  due_days: number; late_fee_percent: string; is_default: boolean; status: "active" | "inactive"
}
export type BookInvoiceSeriesInput = {
  kind: BookInvoiceSeries["kind"]; prefix: string; first_number: number; due_days: number
  late_fee_percent: string; is_default: boolean
}
export async function listBookInvoiceSeries(projectID: string, companyID: string) {
  return boundedRead<BookInvoiceSeries[]>(`${bookPath(projectID)}/invoice-series`, companyID)
}
export async function createBookInvoiceSeries(projectID: string, companyID: string, input: BookInvoiceSeriesInput) {
  return requestJson<BookInvoiceSeries>(`${bookPath(projectID)}/invoice-series`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookInvoiceSeries(projectID: string, companyID: string, seriesID: string,
  input: { due_days: number; late_fee_percent: string; status: "active" | "inactive"; is_default: boolean }) {
  return requestJson<BookInvoiceSeries>(`${bookPath(projectID)}/invoice-series/${encodeURIComponent(seriesID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export type BookBankIdentity = { account_code: string; iban: string }
export async function listBookBankIdentities(projectID: string, companyID: string) {
  return boundedRead<BookBankIdentity[]>(`${bookPath(projectID)}/bank-identities`, companyID)
}
export async function saveBookBankIdentity(projectID: string, companyID: string, accountCode: string, iban: string) {
  return requestJson<BookBankIdentity>(`${bookPath(projectID)}/bank-identities/${encodeURIComponent(accountCode)}`, {
    method: "PUT", headers: await jsonHeaders(companyID), body: JSON.stringify({ iban }),
  }, companyID)
}
export type AccountingReadOptions = { startDate?: string; endDate?: string; periodIDs?: string[]; allDates?: boolean;
  recordID?: string; dateBasis?: "invoice_date" | "turnover_date" | "due_date"; asOf?: string; reportKind?: FinancialReportKind; expectedRevision?: string }
export function accountingReadParams(options: AccountingReadOptions = {}) {
  const params = new URLSearchParams()
  if (options.recordID) { params.set("record_id", options.recordID); return params }
  if (options.startDate) params.set("start_date", options.startDate)
  if (options.endDate) params.set("end_date", options.endDate)
  if (options.asOf) params.set("as_of", options.asOf)
  if (options.periodIDs?.length) params.set("period_ids", options.periodIDs.join(","))
  if (options.dateBasis) params.set("date_basis", options.dateBasis)
  if (options.reportKind) params.set("report_kind", options.reportKind)
  if (options.expectedRevision) params.set("expected_source_revision", options.expectedRevision)
  return params
}
export async function listBookJournals(projectID: string, periodID: string, companyID: string, options: AccountingReadOptions = {}) {
  return boundedRead<BookJournal[]>(`${periodBookPath(projectID, options.allDates ? "ALL" : periodID)}/journals?${accountingReadParams(options)}`, companyID)
}
export async function deleteBookJournalDraft(projectID: string, periodID: string, companyID: string, journalID: string) {
  return requestJson<void>(`${periodBookPath(projectID, periodID)}/journals/${encodeURIComponent(journalID)}`, {
    method: "DELETE", headers: await authHeaders(false, companyID),
  }, companyID)
}
export type BookJournalHistory = { journal_id: string; status?: "draft" | "posted" | "voided" | "deleted"; current_revision: number; reversed_by_journal_id: string | null;
  items: { revision_number: number; actor_id?: string | null; actor_name: string | null; posted_at: string | null; reason: string | null;
    action?: "posted" | "reposted" | "voided" | "returned_to_draft" | "draft_deleted"; recorded_at?: string | null;
    entry: { period_id: string; entry_date: string; description: string; document_number: string | null; entry_type: string;
      is_storno?: boolean; dimension_labels?: Record<string, string>; lines: BookPosting[] } }[] }
export function getBookJournalHistory(projectID: string, periodID: string, companyID: string, journalID: string) {
  return boundedRead<BookJournalHistory>(`${periodBookPath(projectID, periodID)}/journals/${encodeURIComponent(journalID)}/history`, companyID)
}
export async function repostBookJournal(projectID: string, periodID: string, companyID: string, journalID: string,
  input: { request_key: string; expected_revision: number; reason?: string; entry_date: string; description: string;
    document_number?: string; entry_type: "manual" | "opening"; is_storno?: boolean; lines: BookPosting[] }) {
  return requestJson<BookJournal>(`${periodBookPath(projectID, periodID)}/journals/${encodeURIComponent(journalID)}/repost`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function createCorrectedBookJournal(projectID: string, periodID: string, companyID: string, journalID: string,
  input: { request_key: string; expected_revision: number }) {
  return requestJson<BookJournal>(`${periodBookPath(projectID, periodID)}/journals/${encodeURIComponent(journalID)}/corrected-draft`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function voidBookJournal(projectID: string, periodID: string, companyID: string, journalID: string,
  input: { request_key: string; expected_revision: number; reason?: string }) {
  return requestJson<BookJournal>(`${periodBookPath(projectID, periodID)}/journals/${encodeURIComponent(journalID)}/void`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function returnBookJournalToDraft(projectID: string, periodID: string, companyID: string, journalID: string,
  input: { request_key: string; expected_revision: number; reason?: string }) {
  return requestJson<BookJournal>(`${periodBookPath(projectID, periodID)}/journals/${encodeURIComponent(journalID)}/return-to-draft`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function listBookBalances(projectID: string, periodID: string, companyID: string, options: AccountingReadOptions = {}) {
  return boundedRead<BookBalance[]>(`${periodBookPath(projectID, options.allDates ? "ALL" : periodID)}/balances?${accountingReadParams(options)}`, companyID)
}
export async function addBookJournal(projectID: string, periodID: string, companyID: string, input: {
  request_key: string; entry_date: string; description: string; document_number?: string
  entry_type: "manual" | "opening"; is_storno?: boolean; lines: BookPosting[]
}) {
  return requestJson<BookJournal>(`${periodBookPath(projectID, periodID)}/journals`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookJournal(projectID: string, periodID: string, companyID: string, journalID: string, input: {
  entry_date: string; description: string; document_number?: string; entry_type: "manual" | "opening"; is_storno?: boolean; lines: BookPosting[]
}) {
  return requestJson<BookJournal>(`${periodBookPath(projectID, periodID)}/journals/${encodeURIComponent(journalID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export type BulkJournalEntry = { id: string; posting_token: string }
export type BulkJournalResult = { id: string; status: "posted" | "already_posted" | "failed"; code?: string; message?: string }
export async function previewBookBulkPost(projectID: string, periodID: string, companyID: string, entries: BulkJournalEntry[]) {
  return requestJson<{ ready: boolean; issues: { id: string; code: string }[]; count: number }>(`${periodBookPath(projectID, periodID)}/journals/bulk-post-preview`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ entries }),
  }, companyID)
}
export async function postBookJournalBatch(projectID: string, periodID: string, companyID: string, entries: BulkJournalEntry[], selectionIDs: string[]) {
  return requestJson<{ results: BulkJournalResult[]; stop: boolean }>(`${periodBookPath(projectID, periodID)}/journals/bulk-post`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ entries, selection_ids: selectionIDs }),
  }, companyID)
}

export async function postBookJournal(projectID: string, periodID: string, companyID: string, journalID: string) {
  return requestJson<BookJournal>(`${periodBookPath(projectID, periodID)}/journals/${encodeURIComponent(journalID)}/post`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function reverseBookJournal(projectID: string, periodID: string, companyID: string, journalID: string) {
  return requestJson<BookJournal>(`${periodBookPath(projectID, periodID)}/journals/${encodeURIComponent(journalID)}/reverse`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}

export type FinancialReportKind = "trial_balance" | "income_statement" | "balance_sheet"
export type FinancialTrialRow = {
  account_code: string; account_name: string; account_type: BookAccount["account_type"]
  opening_debit: string; opening_credit: string; period_debit: string; period_credit: string
  closing_debit: string; closing_credit: string
}
export type FinancialIncomeRow = { account_code: string; account_name: string; amount: string }
export type FinancialReportBase = {
  report_kind: FinancialReportKind; company_id: string; project_id: string; period_id: string
  period_name: string; start_date: string; end_date: string; currency: "EUR"; source_revision: string; journal_count: number
  source_journal_ids: string[]
}
export type FinancialTrialReport = FinancialReportBase & {
  report_kind: "trial_balance"; rows: FinancialTrialRow[]
  totals: Omit<FinancialTrialRow, "account_code" | "account_name" | "account_type">
  grouped?: { rows: (FinancialTrialRow & { depth: number; is_summary: boolean; is_direct: boolean })[];
    totals: FinancialTrialReport["totals"]; has_parents: boolean }
  checks: { opening_balanced: boolean; turnover_balanced: boolean; closing_balanced: boolean }
}
export type FinancialIncomeReport = FinancialReportBase & {
  report_kind: "income_statement"; revenue: FinancialIncomeRow[]; expenses: FinancialIncomeRow[]
  total_revenue: string; total_expenses: string; net_result: string
}
export type FinancialBalanceReport = FinancialReportBase & {
  report_kind: "balance_sheet"; assets: FinancialIncomeRow[]; liabilities: FinancialIncomeRow[]; equity: FinancialIncomeRow[]
  total_assets: string; total_liabilities: string; total_equity: string; unclosed_earnings: string
  liabilities_and_equity: string; difference: string; checks: { balanced: boolean }
}
export type BookFinancialReport = FinancialTrialReport | FinancialIncomeReport | FinancialBalanceReport
export type BookFinancialSnapshot = {
  id: string; report_kind: FinancialReportKind; period_id: string | null; source_revision: string
  current_revision: string; fresh: boolean; created_at: string; generated_by: string
}
export type BookFinancialSnapshotDetail = BookFinancialSnapshot & { report: BookFinancialReport }
export type BookFinancialPosting = {
  journal_id: string; period_id: string; entry_date: string; description: string; document_number: string | null
  segment: "opening" | "period"; debit: string; credit: string
}
export async function getBookFinancialPreview(projectID: string, periodID: string, companyID: string, kind: FinancialReportKind, options: AccountingReadOptions = {}) {
  return boundedRead<BookFinancialReport>(`${periodBookPath(projectID, options.allDates ? "ALL" : periodID)}/financial-preview?${accountingReadParams({ ...options, reportKind: kind })}`, companyID)
}
export type BookCashMovementReview = { report_kind: "posted_book_cash_account_movement_review"
  company_id: string; project_id: string; period_id: string; start_date: string; end_date: string
  currency: "EUR"; source_revision: string; opening_balance: string; inflow: string; outflow: string
  closing_balance: string; reconciles_with_trial_balance: boolean
  accounts: { account_code: string; account_name: string; kind: "bank" | "cash"
    opening_balance: string; inflow: string; outflow: string; closing_balance: string
    trial_balance_opening: string; trial_balance_period_debit: string
    trial_balance_period_credit: string; trial_balance_closing: string; reconciles: boolean }[]
  rows: { entry_date: string; journal_id: string; account_code: string; debit: string; credit: string
    signed_cash_change: string; statement_provenance: {
      statement_id: string; statement_entry_id: string } | null }[] }
export async function getBookCashMovements(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookCashMovementReview>(`${periodBookPath(projectID, periodID)}/cash-movements`, companyID)
}
export type BookInvoiceAgingReview = { report_kind: "invoice_aging_review"
  company_id: string; project_id: string; period_id: string; start_date: string; end_date: string
  currency: "EUR"; source_revision: string; complete: boolean; limitations: string[]
  exceptions: { invoice_id: string | null; source_kind: string; source_id: string; reason: string }[]
  control_accounts: { account_code: string; ledger_minor: number; subledger_minor: number; difference_minor: number; reconciled: boolean }[]
  sales: BookInvoiceAgingSection; purchases: BookInvoiceAgingSection }
export type BookInvoiceAgingSection = { rows: BookInvoiceAgingRow[]; totals: BookInvoiceAgingBuckets }
export type BookInvoiceAgingBuckets = { not_due: number; "1_30": number; "31_60": number
  "61_90": number; "90_plus": number; unknown_due_date: number; outstanding: number; refund_due: number }
export type BookInvoiceAgingRow = { invoice_id: string | null; invoice_type: "sale" | "purchase"
  record_kind: "invoice" | "opening_item"; opening_item_id: string | null; currency: string
  account_code: string; original_amount_minor: number; original_remaining_minor: number
  number: string; invoice_date: string | null; turnover_date: string | null; due_date: string | null
  days_overdue: number | null; counterparty_id: string | null; counterparty_name: string; journal_id: string | null
  invoice_url: string | null; gross_minor: number; outstanding_minor: number; refund_due_minor: number
  buckets: Omit<BookInvoiceAgingBuckets, "outstanding" | "refund_due">
  sources: { kind: string; id: string; journal_id: string | null; entry_date: string
    amount_minor: number; related_id?: string; filename?: string; evidence?: Record<string, unknown> }[] }
export async function getBookInvoiceAgingReview(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookInvoiceAgingReview>(
    `${periodBookPath(projectID, periodID)}/invoice-aging-review`, companyID)
}
export async function downloadBookInvoiceAgingCsv(projectID: string, periodID: string,
  companyID: string, sourceRevision: string, locale: string) {
  const response = await fetch(`${coworkApiBaseUrl()}${periodBookPath(projectID, periodID)}/invoice-aging-export`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({
      source_revision: sourceRevision, locale,
    }),
  })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "andmera-invoice-aging.csv" }
}
export type BookCloseReadiness = { company_id: string; project_id: string; period_id: string
  cutoff: string; period_locked: boolean; posted_ledger_source_revision: string
  readiness_source_revision: string; status: "unknown" | "needs_review" | "ready"
  ready_for_bookkeeping_close: boolean; required_exceptions: string[]; unknown_checks: string[]
  checks: Record<string, { status: "passed" | "exception" | "unknown" | "not_checked" | "informational"
    count: number | null; source_url: string; sources: Record<string, unknown>[]
    group: "required" | "informational"; source_revision: string | null; detail: unknown }> }
export async function getBookCloseReadiness(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookCloseReadiness>(`${periodBookPath(projectID, periodID)}/close-readiness`, companyID)
}
export type BookAnnualReportPreparation = { status: "needs_evidence"; filing_ready: false
  source_revision: string; fresh: boolean; company_id: string; project_id: string; period_id: string
  period: { start_date: string; end_date: string }; currency: "EUR"; blockers: string[]
  taxonomy: { index_url: string; package_url: string; package_sha256: string; effective_date: string
    income: { role_id: string; role_uri: string; role_schema: string; presentation: string }
    balance: { role_id: string; role_uri: string; role_schema: string; presentation: string
      ou_category: "ordinary" | "small" | "micro" }
    possible_core_concepts: { balance: string[]; income: string[] }; concept_schema: string; scope: string }
  source: { ledger_revision: string; mapping_id: string | null
    mapping_revision: string | null; posted_journal_ids: string[]; unposted_journal_ids: string[] }
  custom_mapping: { purpose: "custom_report_lines_only"; status: "draft" | "approved" | null
    unmapped_account_codes: string[]; incompatible_account_codes: string[]; coverage: Record<string, unknown>
    lines: { section: string; line_code: string; line_label: string; amount: string
      account_codes: string[]; drillthrough: string[] }[] }
  checks: { trial_balance: Record<string, boolean>; balance_sheet: { balanced: boolean }
    annual_result_matches_unclosed_earnings: boolean; custom_line_sections: Record<string, unknown> }
  book_totals: { income: { total_revenue: string; total_expenses: string; net_result: string }
    balance: { total_assets: string; liabilities_and_equity: string; difference: string } }
  comparative: null | { period_id: string; ledger_revision: string; mapping_id: string | null
    mapping_revision: string | null; unmapped_account_codes: string[]; unposted_journal_ids: string[]
    posted_journal_ids: string[]; income: { total_revenue: string; net_result: string }
    balance: { total_assets: string; liabilities_and_equity: string; difference: string } } }
export async function getBookAnnualReportPreparation(projectID: string, periodID: string, companyID: string,
  scheme: "1" | "2", balanceRole: "201010" | "201012" | "201014") {
  const params = new URLSearchParams({ scheme, balance_role: balanceRole })
  return boundedRead<BookAnnualReportPreparation>(
    `${periodBookPath(projectID, periodID)}/annual-report-preparation?${params}`, companyID)
}
export type BookLoan = { id: string; project_id: string; request_key: string
  kind: "borrowed" | "lent"; counterparty_id: string; contract_reference: string
  loan_date: string; amount: string; currency: "EUR"; principal_account_code: string
  status: "draft" | "posted"; disbursement_payment_id: string | null
  disbursement_journal_id: string | null; approved_by: string | null; approved_at: string | null
  repaid: string; outstanding: string
  repayments: { id: string; period_id: string; payment_id: string; repayment_date: string
    amount: string; journal_id: string; approved_by: string }[] }
export type BookLoanInput = { request_key: string; kind: "borrowed" | "lent"
  counterparty_id: string; contract_reference: string; loan_date: string
  amount: string; currency: "EUR"; principal_account_code: string }
export async function listBookLoans(projectID: string, companyID: string) {
  return boundedRead<BookLoan[]>(`${bookPath(projectID)}/loans`, companyID)
}
export async function createBookLoan(projectID: string, companyID: string, input: BookLoanInput) {
  return requestJson<BookLoan>(`${bookPath(projectID)}/loans`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function disburseBookLoan(projectID: string, periodID: string, companyID: string,
  loanID: string, paymentID: string) {
  return requestJson<BookLoan>(`${periodBookPath(projectID, periodID)}/loans/${encodeURIComponent(loanID)}/disburse`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ payment_id: paymentID }),
  }, companyID)
}
export async function repayBookLoan(projectID: string, periodID: string, companyID: string,
  loanID: string, input: { request_key: string; payment_id: string; amount: string }) {
  return requestJson<BookLoan>(`${periodBookPath(projectID, periodID)}/loans/${encodeURIComponent(loanID)}/repayments`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function listBookFinancialSnapshots(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookFinancialSnapshot[]>(`${periodBookPath(projectID, periodID)}/financial-reports`, companyID)
}
export async function getBookFinancialSnapshot(projectID: string, periodID: string, companyID: string, snapshotID: string) {
  return boundedRead<BookFinancialSnapshotDetail>(`${periodBookPath(projectID, periodID)}/financial-reports/${encodeURIComponent(snapshotID)}`, companyID)
}
export async function saveBookFinancialSnapshot(projectID: string, periodID: string, companyID: string, kind: FinancialReportKind, options: AccountingReadOptions = {}) {
  return requestJson<BookFinancialSnapshotDetail>(`${periodBookPath(projectID, options.allDates ? "ALL" : periodID)}/financial-reports`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ report_kind: kind,
      start_date: options.startDate || undefined, end_date: options.endDate || undefined, as_of: options.asOf || undefined,
      period_ids: options.periodIDs, expected_source_revision: options.expectedRevision }),
  }, companyID)
}
export async function getBookFinancialLedger(projectID: string, periodID: string, companyID: string, accountCode: string,
  offset = 0, snapshotID?: string, options: AccountingReadOptions = {}) {
  const params = accountingReadParams(snapshotID ? { reportKind: options.reportKind, expectedRevision: options.expectedRevision } : options)
  params.set("offset", String(offset))
  if (snapshotID) params.set("snapshot_id", snapshotID)
  return boundedRead<{ account_code: string; offset: number; limit: number; total: number; rows: BookFinancialPosting[] }>(
    `${periodBookPath(projectID, options.allDates ? "ALL" : periodID)}/financial-ledger/${encodeURIComponent(accountCode)}?${params}`, companyID)
}
export type BookDimensionSummary = {
  period_id: string; start_date: string; end_date: string; currency: "EUR"; source_revision: string
  rows: { dimension_id: string | null; code: string | null; name: string | null;
    center_type: BookDimension["center_type"] | null; parent_id: string | null;
    revenue: string; expenses: string; net_result: string }[]
  total_revenue: string; total_expenses: string; net_result: string
}
export type BookDimensionPosting = {
  journal_id: string; entry_date: string; description: string;
  account_code: string; account_name: string; debit: string; credit: string
}
export async function getBookDimensionSummary(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookDimensionSummary>(`${periodBookPath(projectID, periodID)}/dimension-summary`, companyID)
}
export async function getBookDimensionLedger(projectID: string, periodID: string, companyID: string,
  dimensionID: string, offset = 0) {
  return boundedRead<{ dimension_id: string | null; offset: number; limit: number; total: number;
    rows: BookDimensionPosting[] }>(`${periodBookPath(projectID, periodID)}/dimension-ledger/${encodeURIComponent(dimensionID)}?offset=${offset}`, companyID)
}
export type BookFixedAsset = {
  source_details?: BookAssetSourceDetails
  id: string; name: string; period_id: string; source_kind: "opening" | "purchase"
  source_number: string | null
  source_journal_id: string | null; source_invoice_line_id: string | null
  acquisition_date: string; depreciation_start_date: string; cost: string; residual: string
  prior_depreciation: string; accumulated: string; impaired: string; book_value: string; useful_life_months: number
  asset_account_code: string; accumulated_account_code: string; expense_account_code: string
  dimension_id: string | null; status: "draft" | "recorded" | "disposed";
  capitalization_journal_id: string | null
  disposal: BookAssetDisposal | null
  impairments: BookAssetImpairment[]
  depreciation_complete: boolean
  effective_residual: string; estimate_changes: BookAssetEstimateChange[]
  runs: { id: string; period_id: string; month_index: number; entry_date: string; amount: string
    journal_id: string | null; correction: BookDepreciationCorrection | null }[]
}
export type BookAssetEstimateChange = { id: string; version: number; period_id: string
  effective_date: string; after_month_index: number; carrying: string; residual: string
  remaining_months: number; reason: string; approved_by: string; approved_at: string }
export type BookAssetEstimateInput = { effective_date: string; residual: string
  remaining_months: number; reason: string }
export type BookAssetEstimatePreview = { asset_id: string; period_id: string; version: number
  effective_date: string; after_month_index: number; carrying: string; residual: string
  remaining_months: number; first_month_charge: string; reason: string }
export type BookAssetEstimateSchedule = { asset_id: string; initial_cost: string
  initial_residual: string; initial_useful_life_months: number; book_value: string
  runs: BookFixedAsset["runs"]; estimate_changes: BookAssetEstimateChange[]
  projection: { month_index: number; entry_date: string; amount: string; closing_carrying: string }[] }
export type BookDepreciationCorrection = { id: string; period_id: string; entry_date: string
  reason: string; revised_amount: string; journal_id: string }
export type BookDepreciationCorrectionInput = { entry_date: string; revised_amount: string; reason: string }
export type BookDepreciationCorrectionPreview = { asset_id: string; run_id: string; period_id: string
  entry_date: string; original_amount: string; revised_amount: string; adjustment: string
  book_before: string; book_after: string; remaining_months: number }
export type BookAssetImpairment = { id: string; period_id: string; entry_date: string; amount: string
  net_book: string; reason: string; expense_account_code: string; contra_account_code: string; journal_id: string
  reversed: boolean; reversal: BookAssetImpairmentReversal | null }
export type BookAssetImpairmentReversal = { id: string; period_id: string; entry_date: string
  reason: string; amount: string; journal_id: string }
export type BookAssetImpairmentReversalInput = { entry_date: string; reason: string }
export type BookAssetImpairmentReversalPreview = { asset_id: string; impairment_id: string; period_id: string
  entry_date: string; amount: string; book_before: string; book_after: string; remaining_months: number }
export type BookAssetImpairmentInput = { amount: string; reason: string; expense_account_code: string
  contra_account_code: string }
export type BookAssetImpairmentPreview = { asset_id: string; period_id: string; entry_date: string
  book_before: string; amount: string; book_after: string; remaining_months: number }
export type BookAssetDisposal = {
  id: string; period_id: string; entry_date: string; disposal_kind: "write_off" | "sale"
  reason: string; accumulated_at_disposal: string; net_book_at_disposal: string
  sale_invoice_line_id: string | null; sale_invoice_number: string | null
  sale_proceeds_at_disposal: string | null; current_sale_proceeds: string | null
  net_sale_result: string | null; loss_account_code: string | null; journal_id: string
  original_loss_account_code: string | null; correction: BookAssetDisposalCorrection | null
}
export type BookAssetDisposalCorrection = { id: string; period_id: string; entry_date: string
  reason: string; amount: string; previous_loss_account_code: string
  new_loss_account_code: string; journal_id: string }
export type BookAssetDisposalCorrectionInput = { entry_date: string; reason: string
  new_loss_account_code: string }
export type BookAssetDisposalCorrectionPreview = { asset_id: string; disposal_id: string
  period_id: string; entry_date: string; net_book: string
  previous_loss_account_code: string; new_loss_account_code: string }
export type BookAssetDisposalInput = { disposal_kind: "write_off" | "sale"; entry_date: string
  reason: string; loss_account_code?: string; sale_invoice_line_id?: string }
export type BookAssetDisposalPreview = {
  asset_id: string; period_id: string; entry_date: string; accumulated: string; net_book: string
  sale_proceeds: string | null; net_sale_result: string | null
}
export type BookAssetSaleSource = { invoice_line_id: string; invoice_number: string; turnover_date: string
  description: string; net_proceeds: string }
export type BookFixedAssetInput = {
  name: string; source_kind: "opening" | "purchase"
  source_journal_id?: string; source_invoice_line_id?: string
  acquisition_date: string; depreciation_start_date: string; cost: string; residual: string
  prior_depreciation: string; useful_life_months: number
  asset_account_code: string; accumulated_account_code: string; expense_account_code: string
  dimension_id?: string
  source_asset_code?: string; source_balance_date?: string | null; source_annual_rate?: string | null
  source_period_depreciation?: string | null; source_period_months?: number | null
  import_item_ids?: string[]; reconciliation_note?: string
}
export type BookAssetSourceDetails = Pick<BookFixedAssetInput, "source_asset_code" | "source_balance_date" |
  "source_annual_rate" | "source_period_depreciation" | "source_period_months" | "import_item_ids" | "reconciliation_note"> & {
  sources?: { filename: string }[]
}
export type BookAssetImportCandidate = { name: string; data: Record<string, unknown>; import_item_ids: string[];
  draft_id?: string; draft_data?: Record<string, unknown>; review_revision?: string; missing_fields?: string[];
  sources: { item_id: string; filename: string; note: string; data?: Record<string, unknown> }[]; conflicts: string[]; existing_asset_id: string | null }
export type BookAssetRegisterRow = { id: string; name: string; source_asset_code: string; status: string;
  acquisition_date: string; opening_cost: string; additions: string; disposals: string; closing_cost: string;
  opening_accumulated: string; depreciation: string; introduced_accumulated: string; disposed_accumulated: string;
  closing_accumulated: string; closing_impairment: string; book_value: string }
export type BookAssetRegister = { company_id: string; project_id: string; start_date: string; end_date: string;
  source_revision: string; rows: BookAssetRegisterRow[]; totals: Record<string, string>; reconciled: boolean;
  reconciliation: { account_code: string; register: string; ledger: string; difference: string }[] }
export type BookAssetBatch = { company_id: string; project_id: string; period_id: string; monthly: boolean;
  source_revision: string; total: string;
  rows: { asset_id: string; name: string; status: "ready" | "posted" | "blocked"; amount: string | null; entry_date: string }[];
  missing: { asset_id: string; name: string; month: string; entry_date: string; month_index: number }[] }
export function getBookAssetImportReview(projectID: string, companyID: string) {
  return boundedRead<{ candidates: BookAssetImportCandidate[]; ignored_subtotals: number }>(`${bookPath(projectID)}/asset-import-review`, companyID)
}
export function saveBookAssetImportDraft(projectID: string, periodID: string, companyID: string, itemID: string,
  revision: string, data: Record<string, string | number | null>) {
  return jsonHeaders(companyID).then((headers) => requestJson<BookAssetImportCandidate>(
    `${periodBookPath(projectID, periodID)}/asset-import-drafts/${encodeURIComponent(itemID)}`, {
      method: "PATCH", headers, body: JSON.stringify({ review_revision: revision, data }),
    }, companyID))
}
export function getBookAssetRegister(projectID: string, companyID: string, start: string, end: string) {
  return boundedRead<BookAssetRegister>(`${bookPath(projectID)}/asset-register?${new URLSearchParams({ start_date: start, end_date: end })}`, companyID)
}
export async function downloadBookAssetRegister(projectID: string, companyID: string, data: BookAssetRegister, format: "pdf" | "xlsx") {
  const query = new URLSearchParams({ start_date: data.start_date, end_date: data.end_date, source_revision: data.source_revision })
  const response = await fetch(`${coworkApiBaseUrl()}${bookPath(projectID)}/asset-register/${format}?${query}`, { headers: await authHeaders(false, companyID) })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(), filename: downloadFilename(response.headers.get("Content-Disposition")) || `asset-register.${format}` }
}
export function getBookAssetBatch(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookAssetBatch>(`${periodBookPath(projectID, periodID)}/asset-depreciation-batch`, companyID)
}
export function postBookAssetBatch(projectID: string, periodID: string, companyID: string, revision: string, assetIDs: string[]) {
  return jsonHeaders(companyID).then((headers) => requestJson<BookAssetBatch>(`${periodBookPath(projectID, periodID)}/asset-depreciation-batch`, {
    method: "POST", headers, body: JSON.stringify({ source_revision: revision, asset_ids: assetIDs }),
  }, companyID))
}
export type BookAssetPurchaseSource = { invoice_line_id: string; invoice_number: string; invoice_date: string
  description: string; expense_account_code: string; dimension_id: string | null; cost: string }
export type BookAssetOpeningSource = { journal_id: string; entry_date: string; asset_account_code: string; available: string }
export type BookDepreciationPreview = { asset_id: string; period_id: string; month_index: number; entry_date: string; amount: string }
export async function listBookFixedAssets(projectID: string, companyID: string) {
  return boundedRead<BookFixedAsset[]>(`${bookPath(projectID)}/assets`, companyID)
}
export async function listBookAssetPurchaseSources(projectID: string, companyID: string) {
  return boundedRead<BookAssetPurchaseSource[]>(`${bookPath(projectID)}/asset-source-purchases`, companyID)
}
export async function listBookAssetOpeningSources(projectID: string, companyID: string) {
  return boundedRead<BookAssetOpeningSource[]>(`${bookPath(projectID)}/asset-source-openings`, companyID)
}
export async function createBookFixedAsset(projectID: string, periodID: string, companyID: string, input: BookFixedAssetInput) {
  return requestJson<BookFixedAsset>(`${periodBookPath(projectID, periodID)}/assets`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookFixedAsset(projectID: string, periodID: string, companyID: string,
  assetID: string, input: BookFixedAssetInput) {
  return requestJson<BookFixedAsset>(`${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function recordBookFixedAsset(projectID: string, periodID: string, companyID: string, assetID: string) {
  return requestJson<BookFixedAsset>(`${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/record`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function previewBookAssetDepreciation(projectID: string, periodID: string, companyID: string, assetID: string) {
  return boundedRead<BookDepreciationPreview>(`${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/depreciation-preview`, companyID)
}
export async function postBookAssetDepreciation(projectID: string, periodID: string, companyID: string, assetID: string) {
  return requestJson<BookFixedAsset>(`${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/depreciate`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function previewBookAssetEstimate(projectID: string, periodID: string, companyID: string,
  assetID: string, input: BookAssetEstimateInput) {
  return requestJson<BookAssetEstimatePreview>(
    `${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/estimate-preview`, {
      method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
    }, companyID)
}
export async function approveBookAssetEstimate(projectID: string, periodID: string, companyID: string,
  assetID: string, input: BookAssetEstimateInput) {
  return requestJson<BookFixedAsset>(
    `${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/estimate-changes`, {
      method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
    }, companyID)
}
export async function getBookAssetEstimateSchedule(projectID: string, companyID: string, assetID: string) {
  return boundedRead<BookAssetEstimateSchedule>(
    `${bookPath(projectID)}/assets/${encodeURIComponent(assetID)}/estimate-schedule`, companyID)
}
export async function previewBookDepreciationCorrection(projectID: string, periodID: string, companyID: string,
  assetID: string, runID: string, input: BookDepreciationCorrectionInput) {
  return requestJson<BookDepreciationCorrectionPreview>(
    `${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/depreciation-runs/${encodeURIComponent(runID)}/correction-preview`, {
      method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
    }, companyID)
}
export async function correctBookAssetDepreciation(projectID: string, periodID: string, companyID: string,
  assetID: string, runID: string, input: BookDepreciationCorrectionInput) {
  return requestJson<BookFixedAsset>(
    `${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/depreciation-runs/${encodeURIComponent(runID)}/correct`, {
      method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
    }, companyID)
}
export async function previewBookAssetImpairment(projectID: string, periodID: string, companyID: string,
  assetID: string, input: BookAssetImpairmentInput) {
  return requestJson<BookAssetImpairmentPreview>(`${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/impairment-preview`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function impairBookFixedAsset(projectID: string, periodID: string, companyID: string,
  assetID: string, input: BookAssetImpairmentInput) {
  return requestJson<BookFixedAsset>(`${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/impair`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function previewBookAssetImpairmentReversal(projectID: string, periodID: string, companyID: string,
  assetID: string, impairmentID: string, input: BookAssetImpairmentReversalInput) {
  return requestJson<BookAssetImpairmentReversalPreview>(
    `${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/impairments/${encodeURIComponent(impairmentID)}/reversal-preview`, {
      method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
    }, companyID)
}
export async function reverseBookAssetImpairment(projectID: string, periodID: string, companyID: string,
  assetID: string, impairmentID: string, input: BookAssetImpairmentReversalInput) {
  return requestJson<BookFixedAsset>(
    `${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/impairments/${encodeURIComponent(impairmentID)}/reverse`, {
      method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
    }, companyID)
}
export async function listBookAssetSaleSources(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookAssetSaleSource[]>(`${periodBookPath(projectID, periodID)}/asset-sale-sources`, companyID)
}
export async function previewBookAssetDisposal(projectID: string, periodID: string, companyID: string,
  assetID: string, input: BookAssetDisposalInput) {
  return requestJson<BookAssetDisposalPreview>(`${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/disposal-preview`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function disposeBookFixedAsset(projectID: string, periodID: string, companyID: string,
  assetID: string, input: BookAssetDisposalInput) {
  return requestJson<BookFixedAsset>(`${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/dispose`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function previewBookAssetDisposalCorrection(projectID: string, periodID: string,
  companyID: string, assetID: string, input: BookAssetDisposalCorrectionInput) {
  return requestJson<BookAssetDisposalCorrectionPreview>(
    `${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/disposal-correction-preview`, {
      method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
    }, companyID)
}
export async function correctBookAssetDisposal(projectID: string, periodID: string,
  companyID: string, assetID: string, input: BookAssetDisposalCorrectionInput) {
  return requestJson<BookFixedAsset>(
    `${periodBookPath(projectID, periodID)}/assets/${encodeURIComponent(assetID)}/dispose-correction`, {
      method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
    }, companyID)
}
export type BookFinancialComparison = {
  report_kind: FinancialReportKind; currency: "EUR"
  periods: { period_id: string; name: string; start_date: string; end_date: string; source_revision: string }[]
  rows: { section: string; account_code: string; account_name: string; values: string[] }[]
  totals: Record<string, string[]>; balanced: boolean[]
  primary_query?: { start_date?: string | null; end_date?: string | null; as_of?: string | null; primary_period_ids?: string[] | null }
}
export async function getBookFinancialComparison(projectID: string, periodID: string, companyID: string,
  kind: FinancialReportKind, periodIDs: string[], options: AccountingReadOptions = {}) {
  const query = accountingReadParams({ ...options, periodIDs: undefined, reportKind: kind })
  if (options.allDates) periodID = "ALL"
  for (const [index, id] of periodIDs.entries()) query.append("period_ids", index === 0 ? periodID : id)
  for (const id of options.periodIDs ?? []) query.append("primary_period_ids", id)
  return boundedRead<BookFinancialComparison>(`${periodBookPath(projectID, periodID)}/financial-comparison?${query}`, companyID)
}
export async function exportBookFinancialComparison(projectID: string, periodID: string, companyID: string,
  comparison: BookFinancialComparison, format: "csv" | "xlsx", locale: string) {
  const response = await fetch(`${coworkApiBaseUrl()}${periodBookPath(projectID, comparison.periods[0]?.period_id ?? periodID)}/financial-comparison/export`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({
      report_kind: comparison.report_kind, period_ids: comparison.periods.map((period) => period.period_id),
      source_revisions: comparison.periods.map((period) => period.source_revision), format, locale, ...comparison.primary_query,
    }),
  })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(), filename: downloadFilename(response.headers.get("Content-Disposition")) || `andmera-report.${format}` }
}
export async function downloadBookFinancialComparisonPdf(projectID: string, periodID: string,
  companyID: string, comparison: BookFinancialComparison, locale: string) {
  const response = await fetch(`${coworkApiBaseUrl()}${periodBookPath(projectID, comparison.periods[0]?.period_id ?? periodID)}/financial-comparison/pdf`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({
      report_kind: comparison.report_kind, period_ids: comparison.periods.map((period) => period.period_id),
      source_revisions: comparison.periods.map((period) => period.source_revision), locale, ...comparison.primary_query,
    }),
  })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || `andmera-${comparison.report_kind}-comparison.pdf` }
}
export type BookReportLineAssignment = { account_code: string
  section: "asset" | "liability" | "equity" | "revenue" | "expense"
  line_code: string; line_label: string }
export type BookReportLineMapping = { id: string; company_id: string; effective_date: string
  status: "draft" | "approved"; lines: BookReportLineAssignment[]; mapping_revision: string
  created_by: string; approved_by: string | null; approved_at: string | null }
export type BookReportLinePreview = { company_id: string; project_id: string; period_id: string
  period_end: string; currency: "EUR"; purpose: "custom_report_lines"; status: "draft" | "ready"
  ready: boolean; fresh: boolean; ledger_revision: string; mapping_revision: string | null
  mapping_id: string | null; mapping_status: "draft" | "approved" | null; posted_journal_count: number
  lines: { section: BookReportLineAssignment["section"]; line_code: string; line_label: string
    account_codes: string[]; period_debit: string; period_credit: string; amount: string }[]
  unmapped_account_codes: string[]; incompatible_account_codes: string[]
  coverage: { posted_account_count: number; mapped_account_count: number; period_debit: string
    period_credit: string; unmapped_period_debit: string; unmapped_period_credit: string }
  reconciliation: { trial_balance_checks: { opening_balanced: boolean; turnover_balanced: boolean
    closing_balanced: boolean }; balance_sheet_checks: { balanced: boolean }
    sections: Record<BookReportLineAssignment["section"], { mapped: string; unmapped: string
      report_total: string; reconciled: boolean }> }
}
export async function listBookReportLineMappings(projectID: string, companyID: string) {
  return boundedRead<BookReportLineMapping[]>(`${bookPath(projectID)}/report-line-mappings`, companyID)
}
export async function createBookReportLineMapping(projectID: string, companyID: string,
  input: { effective_date: string; lines: BookReportLineAssignment[] }) {
  return requestJson<BookReportLineMapping>(`${bookPath(projectID)}/report-line-mappings`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function approveBookReportLineMapping(projectID: string, companyID: string, mappingID: string) {
  return requestJson<BookReportLineMapping>(
    `${bookPath(projectID)}/report-line-mappings/${encodeURIComponent(mappingID)}/approve`, {
      method: "POST", headers: await jsonHeaders(companyID),
    }, companyID)
}
export async function getBookReportLinePreview(projectID: string, periodID: string, companyID: string,
  mappingID?: string, expectedLedgerRevision?: string, expectedMappingRevision?: string) {
  const params = new URLSearchParams()
  if (mappingID) params.set("mapping_id", mappingID)
  if (expectedLedgerRevision) params.set("expected_ledger_revision", expectedLedgerRevision)
  if (expectedMappingRevision) params.set("expected_mapping_revision", expectedMappingRevision)
  return boundedRead<BookReportLinePreview>(
    `${periodBookPath(projectID, periodID)}/report-line-preview${params.size ? `?${params}` : ""}`, companyID)
}
export async function downloadBookFinancialPdf(projectID: string, periodID: string, companyID: string,
  kind: FinancialReportKind, revision: string, locale: string, snapshotID?: string, options: AccountingReadOptions = {}, groupAccounts = true) {
  return downloadBookFinancialFile("pdf", projectID, periodID, companyID, kind, revision, locale, snapshotID, options, groupAccounts)
}

export async function downloadBookFinancialExcel(projectID: string, periodID: string, companyID: string,
  kind: FinancialReportKind, revision: string, locale: string, snapshotID?: string, options: AccountingReadOptions = {}, groupAccounts = true) {
  return downloadBookFinancialFile("xlsx", projectID, periodID, companyID, kind, revision, locale, snapshotID, options, groupAccounts)
}

async function downloadBookFinancialFile(format: "pdf" | "xlsx", projectID: string, periodID: string, companyID: string,
  kind: FinancialReportKind, revision: string, locale: string, snapshotID?: string, options: AccountingReadOptions = {}, groupAccounts = true) {
  if (options.allDates) periodID = "ALL"
  const path = snapshotID
    ? `${periodBookPath(projectID, periodID)}/financial-reports/${encodeURIComponent(snapshotID)}/${format}`
    : `${periodBookPath(projectID, periodID)}/financial-preview/${format}`
  const params = snapshotID ? new URLSearchParams() : accountingReadParams(options)
  params.set("locale", locale)
  params.set("group_accounts", String(groupAccounts))
  if (!snapshotID) { params.set("report_kind", kind); params.set("source_revision", revision) }
  const response = await fetch(`${coworkApiBaseUrl()}${path}?${params}`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || `andmera-${kind}.${format}` }
}

export async function listVatDeclarations(companyID: string, year: number, month: number) {
  return boundedRead<import("./vat-declarations").VatDeclarationList>(`/api/v1/accounting/vat-declarations/${year}/${month}`, companyID)
}
export async function previewVatDeclaration(companyID: string, year: number, month: number, review: import("./vat-declarations").VatDeclarationReview) {
  return requestJson<import("./vat-declarations").VatDeclarationPreview>(`/api/v1/accounting/vat-declarations/${year}/${month}/preview`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(review),
  }, companyID)
}
export async function saveVatDeclaration(companyID: string, year: number, month: number, input: {
  request_key: string; source_revision: string; base_id: string | null; review: import("./vat-declarations").VatDeclarationReview
}) {
  return requestJson<import("./vat-declarations").VatDeclarationDetail>(`/api/v1/accounting/vat-declarations/${year}/${month}/drafts`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function getVatDeclaration(companyID: string, id: string) {
  return boundedRead<import("./vat-declarations").VatDeclarationDetail>(`/api/v1/accounting/vat-declarations/records/${encodeURIComponent(id)}`, companyID)
}
export async function approveVatDeclaration(companyID: string, id: string, contentRevision: string) {
  return requestJson<import("./vat-declarations").VatDeclarationDetail>(`/api/v1/accounting/vat-declarations/records/${encodeURIComponent(id)}/approve`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ content_revision: contentRevision }),
  }, companyID)
}
export async function recordVatSubmission(companyID: string, id: string, input: {
  request_key: string; previous_event_id: string | null; status: string; reference: string; note: string; occurred_at?: string
}) {
  return requestJson<import("./vat-declarations").VatSubmissionEvent>(`/api/v1/accounting/vat-declarations/records/${encodeURIComponent(id)}/submission-events`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function downloadVatDeclaration(companyID: string, id: string, format: "xml" | "xlsx") {
  const response = await fetch(`${coworkApiBaseUrl()}/api/v1/accounting/vat-declarations/records/${encodeURIComponent(id)}/export/${format}`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(), filename: downloadFilename(response.headers.get("Content-Disposition")) || `kmd.${format}` }
}

export type BookVatTreatment = "sale_domestic_taxable" | "sale_zero_domestic" | "sale_zero_export" | "sale_exempt" |
  "purchase_domestic_deductible" | "purchase_zero"
export type BookVatRegistration = { id: string; vat_number: string; start_date: string; end_date: string | null }
export type BookVatEvidence = {
  source_type: "invoice" | "credit" | "advance_receipt" | "advance_application" |
    "advance_application_reversal" | "advance_refund";
  source_id: string; source_number: string; date: string
  original_invoice_id: string; original_invoice_number: string; invoice_line_id: string
  line_description: string; counterparty_id: string; counterparty_name: string
  invoice_type: "sale" | "purchase"; treatment: BookVatTreatment | null
  net: string; vat: string
}
export type BookVatWorksheet = {
  period_id: string; company_id: string; project_id: string; start_date: string; end_date: string
  registration: BookVatRegistration | null; status: "needs_registration" | "needs_classification" | "needs_source_review" | "ready_for_review"
  unclassified_count: number; source_revision: string; currency: "EUR"
  output_vat: string; input_vat: string; provisional_vat_payable: string
  categories: Record<string, { net: string; vat: string }>; evidence: BookVatEvidence[]
  unmapped_vat_postings: { journal_id: string; date: string; description: string;
    account_code: string; debit: string; credit: string }[]
  unapplied_advance_candidates: { advance_id: string; invoice_id: string; invoice_number: string;
    buyer_name: string; unused_at_period_end: string }[]
}
export type BookVatWorksheetSnapshot = {
  id: string; period_id: string; source_revision: string; current_revision: string; fresh: boolean
  created_at: string; generated_by: string
}
export type BookVatWorksheetSnapshotDetail = BookVatWorksheetSnapshot & { worksheet: BookVatWorksheet }
export type BookVatSourceReview = { period_id: string; currency: "EUR"; source_revision: string
  ready_for_review: boolean
  documents: { source_type: BookVatEvidence["source_type"]; source_id: string; source_number: string
    date: string; period_id: string; journal_id: string; original_invoice_id: string | null
    net: string; vat: string; treatments: string[]; classification: "covered" | "needs_review"
    reasons_for_review: string[] }[]
  vat_account_activity: { posting_id: string; journal_id: string; period_id: string; date: string
    account_code: string; debit: string; credit: string
    source_ids: { source_type: string; source_id: string }[]
    classification: "source_linked" | "other_posted_activity"; reason_for_review: string | null }[] }
export type BookKmdPreparation = { period_id: string; company_id: string; project_id: string
  start_date: string; end_date: string; evidence_cutoff: string; currency: "EUR"
  status: "needs_evidence"; filing_ready: false; source_revision: string
  official_schema: { version: "KMD6"; effective_from: string; effective_to: string | null
    reference: string; xsd_url: string }
  taxPayerRegCode: string | null; vat_worksheet_revision: string
  vat_source_review_revision: string; vat_worksheet_status: string
  vat_source_review_ready: boolean
  documents: { source_type: "invoice" | "credit"; source_id: string; number: string
    invoice_type: "sale" | "purchase"; invoice_date?: string; turnover_date?: string
    credit_date?: string; original_invoice_id?: string; original_invoice_number?: string
    period_id: string; journal_id: string; currency: string
    counterparty: { counterparty_id: string; name: string; registry_code: string | null
      vat_number: string | null; country: string | null }
    net_minor: number; vat_minor: number; date_mismatch?: boolean
    lines: { invoice_line_id: string; description: string; net_minor: number; vat_minor: number
      treatment: string | null; vat_rate_bp: number }[] }[]
  prepayment_invoices: { id: string; number: string; invoice_date: string; net_minor: number
    vat_minor: number; requires_advance_invoice_completeness_review: boolean }[]
  unresolved_prepayment_receipts: { advance_id: string; receipt_date: string; amount_minor: number }[]
  advance_evidence: BookVatEvidence[]
  vat_posting_exceptions: { documents: BookVatSourceReview["documents"]
    activity: BookVatSourceReview["vat_account_activity"]; unapplied_advance_candidates: unknown[] }
  inf_threshold_minor: number; inf_threshold_candidates: {
    side: "A" | "B"; counterparty: BookKmdPreparation["documents"][number]["counterparty"]
    invoice_net_minor: number; credit_net_minor_abs: number
    invoice_threshold_candidate: boolean; credit_threshold_candidate: boolean
    invoices: string[]; credits: string[]; legal_inclusion_validated: false }[]
  inf_legally_validated_inclusions: never[]; missing_evidence: Record<string, boolean>
  activity_flags: { noSales: null; noPurchases: null } }
export async function getBookKmdPreparation(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookKmdPreparation>(`${periodBookPath(projectID, periodID)}/kmd-preparation`, companyID)
}
export async function listBookVatRegistrations(projectID: string, companyID: string) {
  return boundedRead<BookVatRegistration[]>(`${bookPath(projectID)}/vat-registrations`, companyID)
}
export async function createBookVatRegistration(projectID: string, companyID: string,
  input: { vat_number: string; start_date: string; end_date?: string }) {
  return requestJson<BookVatRegistration>(`${bookPath(projectID)}/vat-registrations`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function endBookVatRegistration(projectID: string, companyID: string,
  registrationID: string, endDate: string | null) {
  return requestJson<BookVatRegistration>(`${bookPath(projectID)}/vat-registrations/${encodeURIComponent(registrationID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify({ end_date: endDate }),
  }, companyID)
}
export async function setBookVatTreatment(projectID: string, companyID: string, invoiceLineID: string,
  treatment: BookVatTreatment) {
  return requestJson<{ invoice_line_id: string; treatment: BookVatTreatment }>(
    `${bookPath(projectID)}/invoice-lines/${encodeURIComponent(invoiceLineID)}/vat-treatment`, {
      method: "PUT", headers: await jsonHeaders(companyID), body: JSON.stringify({ treatment }),
    }, companyID)
}
export async function getBookVatWorksheet(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookVatWorksheet>(`${periodBookPath(projectID, periodID)}/vat-worksheet`, companyID)
}
export async function getBookVatSourceReview(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookVatSourceReview>(`${periodBookPath(projectID, periodID)}/vat-source-review`, companyID)
}
export async function listBookVatWorksheets(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookVatWorksheetSnapshot[]>(`${periodBookPath(projectID, periodID)}/vat-worksheets`, companyID)
}
export async function getBookVatWorksheetSnapshot(projectID: string, periodID: string, companyID: string, snapshotID: string) {
  return boundedRead<BookVatWorksheetSnapshotDetail>(
    `${periodBookPath(projectID, periodID)}/vat-worksheets/${encodeURIComponent(snapshotID)}`, companyID)
}
export async function saveBookVatWorksheet(projectID: string, periodID: string, companyID: string) {
  return requestJson<BookVatWorksheetSnapshotDetail>(`${periodBookPath(projectID, periodID)}/vat-worksheets`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}

export type BookPerson = {
  id: string; first_name: string; last_name: string; country: string; personal_id: string | null
  birth_date: string | null; email: string | null; phone: string | null; bank_iban: string | null
  status: "active" | "inactive"
  employee_id?: string | null
  needs_profile?: boolean
  details?: BookPersonDetails
}
export type BookPersonInput = { first_name: string; last_name: string; country: string
  personal_id?: string | null; birth_date?: string | null; email?: string; phone?: string; bank_iban?: string; employee_id?: string; details?: BookPersonDetails }
export type BookPersonDetails = { address?: string | null; bank_name?: string | null; information?: string | null; notes?: string | null
  payday?: number | null; holiday_payment?: "before_leave" | "next_salary" | null; car_use?: boolean | null }
export type BookPayrollProfileInput = { effective_from: string; effective_to?: string | null; replaces_profile_id?: string | null
  social_security_country?: string | null; basic_exemption_type?: "general" | "pension_age" | "unknown"; employee_unemployment_insured?: boolean | null
  tax_residency: "resident" | "nonresident" | "unknown"; residency_country: string | null
  exemption_application: boolean | null; monthly_exemption: string | null; social_minimum_applies: boolean | null
  social_minimum_exception: string | null; pension_status: "contributing" | "not_contributing" | "unknown"
  pension_rate: 0 | 2 | 4 | 6 | null; pension_effective_from: string | null; pension_effective_to: string | null
  pensioner: boolean | null; employee_unemployment_exempt_from: string | null; evidence_reference: string }
export type BookPayrollProfile = BookPayrollProfileInput & { id: string; person_id: string; revision: number; superseded: boolean; recorded_by: string; recorded_at: string }
export type BookCompensationTypeInput = { code: string; name: string; category: "employment" | "board" | "contractor" | "other"; payment_code: string | null; status: "active" | "inactive" }
export type BookCompensationType = BookCompensationTypeInput & { id: string }
export type BookTsdPaymentCode = { code: string; name_et: string; suggested_category: BookCompensationTypeInput["category"]; section: "Ia" | "II"; reference_date: string; source_title: string
  calculation: { supported: boolean; rule_version: string | null; effective_from: string | null; effective_to: string | null } }
export type BookGarnishmentInput = { recipient_id: string; effective_from: string; effective_to: string | null; amount: string; description: string; status: "active" | "inactive" }
export type BookGarnishment = BookGarnishmentInput & { id: string; person_id: string }
export async function listBookPayrollProfiles(projectID: string, companyID: string, personID: string) {
  return boundedRead<BookPayrollProfile[]>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}/payroll-profiles`, companyID)
}
export async function saveBookPayrollProfile(projectID: string, companyID: string, personID: string, input: BookPayrollProfileInput) {
  return requestJson<BookPayrollProfile>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}/payroll-profiles`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function listBookCompensationTypes(projectID: string, companyID: string) {
  return boundedRead<BookCompensationType[]>(`${personnelBookPath(projectID)}/compensation-types`, companyID)
}
export async function listBookTsdPaymentCodes(projectID: string, companyID: string) {
  return boundedRead<BookTsdPaymentCode[]>(`${personnelBookPath(projectID)}/tsd-payment-codes`, companyID)
}
export async function saveBookCompensationType(projectID: string, companyID: string, input: BookCompensationTypeInput, id?: string) {
  return requestJson<BookCompensationType>(`${personnelBookPath(projectID)}/compensation-types${id ? `/${encodeURIComponent(id)}` : ""}`, {
    method: id ? "PATCH" : "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function deleteBookCompensationType(projectID: string, companyID: string, id: string) {
  return requestJson<void>(`${personnelBookPath(projectID)}/compensation-types/${encodeURIComponent(id)}`, {
    method: "DELETE", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function listBookGarnishments(projectID: string, companyID: string, personID: string) {
  return boundedRead<BookGarnishment[]>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}/garnishments`, companyID)
}
export async function saveBookGarnishment(projectID: string, companyID: string, personID: string, input: BookGarnishmentInput, id?: string) {
  return requestJson<BookGarnishment>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}/garnishments${id ? `/${encodeURIComponent(id)}` : ""}`, {
    method: id ? "PATCH" : "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function checkBookPersonPension(projectID: string, companyID: string, personID: string) {
  return requestJson<{ person_id: string; employee_id: string; retrieved_at?: string; evidence: { evidence_text: string; evidence_sha256: string } }>(
    `${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}/pension-check`, {
      method: "POST", headers: await jsonHeaders(companyID),
    }, companyID)
}
export type BookContract = {
  id: string; person_id: string; position: string; start_date: string; end_date: string | null
  workload_percent: string; monthly_gross: string; status: "active" | "inactive"
  compensation_type_id?: string | null; suspension_start?: string | null; suspension_end?: string | null
}
export type BookContractInput = { position: string; start_date: string; end_date?: string; workload_percent: string; monthly_gross: string
  compensation_type_id?: string | null; suspension_start?: string | null; suspension_end?: string | null }
export type BookPersonAbsenceInput = {
  person_id: string; contract_id: string; kind: "annual_leave" | "sick_leave" | "unpaid_leave" | "other_absence"
  start_date: string; end_date: string; reason?: string; reference?: string
}
export type BookPersonAbsence = BookPersonAbsenceInput & {
  id: string; status: "draft" | "approved" | "rejected"
  approved_at: string | null; approved_by: string | null
  rejected_at: string | null; rejected_by: string | null
}
export type BookPayrollInput = {
  tax_calculation?: BookTaxSelection | null
  person_id: string; contract_id: string; recording_date: string; gross: string
  income_tax: string; employee_unemployment: string; employee_pension: string
  employer_social: string; employer_unemployment: string; garnishment: string
  wage_expense_code: string; employer_tax_expense_code: string; net_payable_code: string
  tax_payable_code: string; garnishment_payable_code?: string
}
export type BookPayrollRun = Omit<BookPayrollInput, "tax_calculation"> & {
  tax_calculation?: BookTaxCalculation | null
  id: string; person_name: string; position: string; period_id: string; net: string
  status: "draft" | "confirmed" | "voided"; journal_id: string | null
  net_paid: string; net_outstanding: string; payment_status: "not_applicable" | "unpaid" | "partial" | "paid"
  wage_payments: { payment_id: string; payment_date: string; amount: string; journal_id: string }[]
  leave_review_snapshot: { id: string; kind: BookPersonAbsenceInput["kind"];
    start_date: string; end_date: string }[] | null
  leave_reviewed_at: string | null; leave_review_stale: boolean
}
export type BookTaxParameters = { payment_date: string; monthly_exemption_used: string | null; social_minimum_base: string | null; social_minimum_basis: string | null; sickness_eligible: boolean }
export type BookTaxSelection = { parameters: BookTaxParameters; expected_revision: string }
export type BookTaxPreviewInput = Pick<BookPayrollInput, "person_id" | "contract_id" | "recording_date" | "gross" | "garnishment"> & { parameters: BookTaxParameters; run_id?: string }
export type BookTaxCalculation = { status: "calculated" | "blocked"; issues: string[]; payment_code: string | null; rule_version: string; source_revision: string; request: BookTaxPreviewInput
  amounts: (Pick<BookPayrollInput, "gross" | "income_tax" | "employee_unemployment" | "employee_pension" | "employer_social" | "employer_unemployment" | "garnishment"> & { net: string; employer_cost: string }) | null
  bases?: { income_tax: string; social_tax: string; unemployment: string; pension: string; allowance_applied: string; allowance_limit: string }
  rules: { version: string; effective_from: string; effective_to: string; sources: string[] } }
export async function calculateBookPayrollTax(projectID: string, periodID: string, companyID: string, input: BookTaxPreviewInput) {
  return requestJson<BookTaxCalculation>(`${personnelBookPath(projectID)}/periods/${encodeURIComponent(periodID)}/payroll-tax-preview`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export type BookPaidCompensationReview = { period_id: string; currency: "EUR"
  evidence_type: "accountant_payroll_payments"; source_revision: string; total_paid_net: string
  rows: { run_id: string; source_period_id: string; source_recording_date: string
    payroll_journal_id: string; person_id: string; person_name: string; paid_gross: string | null
    paid_net: string; source_run_net: string; source_run_net_paid: string
    source_run_net_remaining: string; leave_review_stale: boolean; exceptions: string[]
    payments: { allocation_id: string; payment_id: string; payment_date: string
      payment_journal_id: string; settlement_journal_id: string; paid_net: string }[] }[] }
export type BookTsdPaymentPreparation = { period_id: string
  payment_period: { start_date: string; end_date: string }; currency: "EUR"
  format_family: "data_based_tsd_2026_10"; status: "needs_evidence"; filing_ready: false
  source_revision: string; source_evidence_revision: string; company_registry_id: string | null
  total_paid_net: string; missing_required_choices: string[]
  official_reference: { publisher: string; subject: string; example_url: string }
  unallocated_payments: { payment_id: string; payment_date: string; unallocated_net: string }[]
  rows: (BookPaidCompensationReview["rows"][number] & {
    person_identifier_snapshot: null
    source_accounting_period: { start_date: string; end_date: string }
    source_run_withholding: null | { income_tax: string; employee_unemployment: string
      employee_pension: string }
    source_employer_contributions: null | { employer_social: string; employer_unemployment: string }
    missing_required_choices: string[] })[] }
export async function getBookTsdPaymentPreparation(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookTsdPaymentPreparation>(
    `${periodPayrollPath(projectID, periodID)}/tsd-payment-preparation`, companyID)
}
const personnelBookPath = (projectID: string) => `${bookPath(projectID)}/personnel`
const periodPayrollPath = (projectID: string, periodID: string) => `${personnelBookPath(projectID)}/periods/${encodeURIComponent(periodID)}`
export async function listBookPersons(projectID: string, companyID: string) {
  return boundedRead<BookPerson[]>(`${personnelBookPath(projectID)}/persons`, companyID)
}
export async function listBookPersonnelRegister(projectID: string, companyID: string) {
  return boundedRead<BookPerson[]>(`${personnelBookPath(projectID)}/register`, companyID)
}
export async function deleteBookPerson(projectID: string, companyID: string, personID: string) {
  return requestJson<void>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}`, {
    method: "DELETE", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function deleteBookPersonnelEmployee(projectID: string, companyID: string, employeeID: string) {
  return requestJson<void>(`${personnelBookPath(projectID)}/register/employees/${encodeURIComponent(employeeID)}`, {
    method: "DELETE", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function createBookPerson(projectID: string, companyID: string, input: BookPersonInput) {
  return requestJson<BookPerson>(`${personnelBookPath(projectID)}/persons`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookPerson(projectID: string, companyID: string, personID: string,
  input: Omit<BookPersonInput, "country" | "employee_id"> & { country?: string }) {
  return requestJson<BookPerson>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function linkBookPersonEmployee(projectID: string, companyID: string, personID: string) {
  return requestJson<BookPerson>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}/employee`, {
    method: "PUT", headers: await jsonHeaders(companyID), body: JSON.stringify({}),
  }, companyID)
}
export async function unlinkBookPersonEmployee(projectID: string, companyID: string, personID: string) {
  return requestJson<BookPerson>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}/employee`, {
    method: "DELETE", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function setBookPersonStatus(projectID: string, companyID: string, personID: string,
  status: "active" | "inactive") {
  return requestJson<BookPerson>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}/status`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify({ status }),
  }, companyID)
}
export async function listBookContracts(projectID: string, companyID: string) {
  return boundedRead<BookContract[]>(`${personnelBookPath(projectID)}/contracts`, companyID)
}
export async function createBookContract(projectID: string, companyID: string, personID: string,
  input: BookContractInput) {
  return requestJson<BookContract>(`${personnelBookPath(projectID)}/persons/${encodeURIComponent(personID)}/contracts`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookContract(projectID: string, companyID: string, contractID: string,
  input: BookContractInput) {
  return requestJson<BookContract>(`${personnelBookPath(projectID)}/contracts/${encodeURIComponent(contractID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function deleteBookContract(projectID: string, companyID: string, contractID: string) {
  return requestJson<void>(`${personnelBookPath(projectID)}/contracts/${encodeURIComponent(contractID)}`, {
    method: "DELETE", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function endBookContract(projectID: string, companyID: string, contractID: string, endDate: string) {
  return requestJson<BookContract>(`${personnelBookPath(projectID)}/contracts/${encodeURIComponent(contractID)}/end`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify({ end_date: endDate }),
  }, companyID)
}
export async function listBookPersonAbsences(projectID: string, companyID: string, personID?: string) {
  const query = personID ? `?person_id=${encodeURIComponent(personID)}` : ""
  return boundedRead<BookPersonAbsence[]>(`${personnelBookPath(projectID)}/absences${query}`, companyID)
}
export async function listBookPayrollApprovedAbsences(projectID: string, periodID: string,
  companyID: string, personID?: string) {
  const query = personID ? `?person_id=${encodeURIComponent(personID)}` : ""
  return boundedRead<BookPersonAbsence[]>(`${periodPayrollPath(projectID, periodID)}/approved-absences${query}`, companyID)
}
export async function createBookPersonAbsence(projectID: string, companyID: string, input: BookPersonAbsenceInput) {
  return requestJson<BookPersonAbsence>(`${personnelBookPath(projectID)}/absences`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookPersonAbsence(projectID: string, companyID: string, absenceID: string,
  input: BookPersonAbsenceInput) {
  return requestJson<BookPersonAbsence>(`${personnelBookPath(projectID)}/absences/${encodeURIComponent(absenceID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function approveBookPersonAbsence(projectID: string, companyID: string, absenceID: string) {
  return requestJson<BookPersonAbsence>(`${personnelBookPath(projectID)}/absences/${encodeURIComponent(absenceID)}/approve`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function rejectBookPersonAbsence(projectID: string, companyID: string, absenceID: string) {
  return requestJson<BookPersonAbsence>(`${personnelBookPath(projectID)}/absences/${encodeURIComponent(absenceID)}/reject`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function listBookPayrollRuns(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookPayrollRun[]>(`${periodPayrollPath(projectID, periodID)}/payroll-runs`, companyID)
}
export async function getBookPaidCompensationReview(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookPaidCompensationReview>(
    `${periodPayrollPath(projectID, periodID)}/paid-compensation-review`, companyID)
}
export async function createBookPayrollRun(projectID: string, periodID: string, companyID: string,
  input: BookPayrollInput & { request_key: string }) {
  return requestJson<BookPayrollRun>(`${periodPayrollPath(projectID, periodID)}/payroll-runs`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookPayrollRun(projectID: string, periodID: string, companyID: string,
  runID: string, input: BookPayrollInput) {
  return requestJson<BookPayrollRun>(`${periodPayrollPath(projectID, periodID)}/payroll-runs/${encodeURIComponent(runID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function confirmBookPayrollRun(projectID: string, periodID: string, companyID: string,
  runID: string, reviewedAbsenceIDs: string[]) {
  return requestJson<BookPayrollRun>(`${periodPayrollPath(projectID, periodID)}/payroll-runs/${encodeURIComponent(runID)}/confirm`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({
      leave_reviewed: true, reviewed_absence_ids: reviewedAbsenceIDs,
    }),
  }, companyID)
}
export async function deleteBookPayrollDraft(projectID: string, periodID: string, companyID: string, runID: string) {
  return requestJson<void>(`${periodPayrollPath(projectID, periodID)}/payroll-runs/${encodeURIComponent(runID)}`, {
    method: "DELETE", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function voidBookPayrollRun(projectID: string, periodID: string, companyID: string, runID: string) {
  return requestJson<BookPayrollRun>(`${periodPayrollPath(projectID, periodID)}/payroll-runs/${encodeURIComponent(runID)}/void`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function reviewBookPayrollLeave(projectID: string, periodID: string, companyID: string,
  runID: string, reviewedAbsenceIDs: string[]) {
  return requestJson<BookPayrollRun>(`${periodPayrollPath(projectID, periodID)}/payroll-runs/${encodeURIComponent(runID)}/leave-review`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ leave_reviewed: true, reviewed_absence_ids: reviewedAbsenceIDs }),
  }, companyID)
}
export async function downloadConfirmedPayrollPayslipPdf(projectID: string, periodID: string,
  companyID: string, runID: string) {
  const response = await fetch(
    `${coworkApiBaseUrl()}${periodPayrollPath(projectID, periodID)}/payroll-runs/${encodeURIComponent(runID)}/payslip.pdf`, {
      headers: await authHeaders(false, companyID),
    })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "andmera-payslip.pdf" }
}
export type BookAvailableWagePayment = {
  id: string; period_id: string; payment_date: string; description: string; reference_number: string | null
  amount: string; unallocated: string; can_match: boolean
}
export async function listAvailableBookWagePayments(projectID: string, companyID: string) {
  return boundedRead<BookAvailableWagePayment[]>(`${personnelBookPath(projectID)}/available-wage-payments`, companyID)
}
export async function allocateBookWagePayment(projectID: string, paymentPeriodID: string, companyID: string,
  paymentID: string, input: { request_key: string; run_id: string; amount: string }) {
  return requestJson<BookPayrollRun>(`${personnelBookPath(projectID)}/periods/${encodeURIComponent(paymentPeriodID)}/payments/${encodeURIComponent(paymentID)}/wage-allocations`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}

export type BillingCounterparty = {
  id: string; name: string; registry_code: string | null; vat_number: string | null
  country: string; address: string | null; email: string | null;
  is_buyer: boolean; is_supplier: boolean; status: "active" | "inactive"
}
export type BillingLine = {
  id?: string; product_id?: string | null; dimension_id?: string | null;
  description: string; unit?: string | null;
  account_code: string; vat_account_code?: string | null
  quantity: string; unit_price: string; vat_rate: string; net?: string; vat?: string
  available_quantity?: string
}
export type BillingInvoice = {
  revision_number?: number
  pdf_language?: "en" | "et"; pdf_template?: "standard" | "compact"
  id: string; period_id: string; invoice_type: "sale" | "purchase"; number: string
  series_id: string | null; series_sequence: number | null
  invoice_date: string; turnover_date: string; due_date: string
  counterparty_id: string; counterparty_name: string; counterparty_address: string | null;
  counterparty_registry_code: string | null; counterparty_vat_number: string | null;
  counterparty_country: string | null
  issuer_name: string | null; issuer_vat_number: string | null; issuer_registry_code: string | null
  issuer_country: string | null; issuer_address: string | null
  issuer_identity_source: "snapshot" | "current_profile_fallback" | "pending_confirmation"
  company_bank_account_code: string | null; company_iban: string | null
  currency: "EUR"; settlement_account_code: string
  reference_number: string | null; status: "draft" | "confirmed" | "cancelled"; net: string; vat: string; gross: string
  journal_id: string | null; lines: BillingLine[]
  paid: string; refunded: string; advance_applied: string; supplier_advance_applied: string
  outstanding: string; credited: string; effective_gross: string; refund_due: string
  payment_status: "not_applicable" | "unpaid" | "partial" | "paid" | "credited" | "refund_due"
}
export type BillingInvoiceInput = {
  invoice_type: "sale" | "purchase"; number: string; series_id?: string
  invoice_date: string; turnover_date: string; due_date: string
  counterparty_id: string; currency: "EUR"; settlement_account_code: string; reference_number?: string
  company_bank_account_code?: string | null
  lines: BillingLine[]
}
const billingPath = (projectID: string) => `/api/v1/accounting/billing/projects/${encodeURIComponent(projectID)}`
const billingPeriodPath = (projectID: string, periodID: string) => `${billingPath(projectID)}/periods/${encodeURIComponent(periodID)}`

export async function listBillingCounterparties(projectID: string, companyID: string) {
  return boundedRead<BillingCounterparty[]>(`${billingPath(projectID)}/counterparties`, companyID)
}
export type BillingCounterpartyInput = {
  name: string; registry_code?: string; vat_number?: string; country: string; address?: string; email?: string
  is_buyer: boolean; is_supplier: boolean
}
export async function createBillingCounterparty(projectID: string, companyID: string, input: BillingCounterpartyInput) {
  return requestJson<BillingCounterparty>(`${billingPath(projectID)}/counterparties`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBillingCounterparty(projectID: string, companyID: string,
  partyID: string, input: Partial<BillingCounterpartyInput>) {
  return requestJson<BillingCounterparty>(`${billingPath(projectID)}/counterparties/${encodeURIComponent(partyID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function setBillingCounterpartyStatus(projectID: string, companyID: string, partyID: string, status: "active" | "inactive") {
  return requestJson<BillingCounterparty>(`${billingPath(projectID)}/counterparties/${encodeURIComponent(partyID)}/status`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify({ status }),
  }, companyID)
}
export async function listBillingInvoices(projectID: string, periodID: string, companyID: string, includeCancelled = false, options: AccountingReadOptions = {}) {
  const params = accountingReadParams(options)
  if (includeCancelled) params.set("include_cancelled", "true")
  return boundedRead<BillingInvoice[]>(`${billingPeriodPath(projectID, options.allDates ? "ALL" : periodID)}/invoices?${params}`, companyID)
}
export async function cancelBillingInvoice(projectID: string, periodID: string, companyID: string, invoiceID: string) {
  return requestJson<BillingInvoice>(`${billingPeriodPath(projectID, periodID)}/invoices/${encodeURIComponent(invoiceID)}/cancel`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function reopenBillingInvoice(projectID: string, periodID: string, companyID: string, invoiceID: string, journalID: string) {
  return requestJson<BillingInvoice>(`${billingPeriodPath(projectID, periodID)}/invoices/${encodeURIComponent(invoiceID)}/reopen`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ expected_journal_id: journalID }),
  }, companyID)
}
export async function voidBillingInvoice(projectID: string, periodID: string, companyID: string, invoiceID: string, journalID: string) {
  return requestJson<BillingInvoice>(`${billingPeriodPath(projectID, periodID)}/invoices/${encodeURIComponent(invoiceID)}/void`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ expected_journal_id: journalID }),
  }, companyID)
}
export type BillingInvoiceRevision = { id: string; action: "reopened" | "cancelled"; recorded_at: string;
  original_journal_id: string; reversal_journal_id: string; invoice: BillingInvoice }
export async function listBillingInvoiceHistory(projectID: string, companyID: string, invoiceID: string) {
  return boundedRead<BillingInvoiceRevision[]>(`${billingPath(projectID)}/invoices/${encodeURIComponent(invoiceID)}/history`, companyID)
}
export async function listOpenBillingInvoices(projectID: string, companyID: string) {
  return boundedRead<BillingInvoice[]>(`${billingPath(projectID)}/open-invoices`, companyID)
}
export type OverviewInvoiceReference = Pick<BillingInvoice, "id" | "period_id" | "invoice_type" | "number" | "counterparty_name" | "due_date" | "currency">
export type OverviewInvoiceResponse = { company_id: string; project_id: string; today: string; currency: "EUR" }
export type OverviewInvoiceTotals = OverviewInvoiceResponse & {
  receivables_minor: string; payables_minor: string; overdue_receivables_minor: string; overdue_payables_minor: string
  sales_count: number; purchases_count: number
  buckets: { from: number; to: number; incoming_minor: string; outgoing_minor: string }[]
  sales: Omit<OverviewInvoicePage, keyof OverviewInvoiceResponse>; purchases: Omit<OverviewInvoicePage, keyof OverviewInvoiceResponse>
}
export type OverviewInvoicePage = OverviewInvoiceResponse & {
  rows: (OverviewInvoiceReference & { days: number; remaining_minor: string })[]
  total: number; amount_minor: string; page: number; pages: number; start: number; end: number
}
export async function getOverviewInvoiceTotals(projectID: string, companyID: string) {
  return boundedRead<OverviewInvoiceTotals>(`${billingPath(projectID)}/overview-invoices`, companyID)
}
export async function getOverviewInvoicePage(projectID: string, companyID: string, kind: "sale" | "purchase",
  filter: { search: string; due: string; sort: string; page: number }) {
  const query = new URLSearchParams({ invoice_type: kind, search: filter.search, due: filter.due, sort: filter.sort, page: String(filter.page) })
  return boundedRead<OverviewInvoicePage>(`${billingPath(projectID)}/overview-invoices?${query}`, companyID)
}
export async function listCreditableInvoices(projectID: string, companyID: string) {
  return boundedRead<BillingInvoice[]>(`${billingPath(projectID)}/confirmed-invoices`, companyID)
}
export async function createBillingInvoice(projectID: string, periodID: string, companyID: string, input: BillingInvoiceInput & { request_key: string }) {
  return requestJson<BillingInvoice>(`${billingPeriodPath(projectID, periodID)}/invoices`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBillingInvoice(projectID: string, periodID: string, companyID: string, invoiceID: string, input: Omit<BillingInvoiceInput, "invoice_type"> & { expected_revision?: number }) {
  return requestJson<BillingInvoice>(`${billingPeriodPath(projectID, periodID)}/invoices/${encodeURIComponent(invoiceID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function confirmBillingInvoice(projectID: string, periodID: string, companyID: string, invoiceID: string, expectedRevision?: number) {
  return requestJson<BillingInvoice>(`${billingPeriodPath(projectID, periodID)}/invoices/${encodeURIComponent(invoiceID)}/confirm${expectedRevision !== undefined ? `?expected_revision=${expectedRevision}` : ""}`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function downloadConfirmedBillingInvoicePdf(projectID: string, companyID: string, invoiceID: string, preview = false) {
  const response = await fetch(`${coworkApiBaseUrl()}${billingPath(projectID)}/invoices/${encodeURIComponent(invoiceID)}/pdf${preview ? "?preview=true" : ""}`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "andmera-invoice.pdf" }
}
export type InvoicePdfOptions = { pdf_language: "en" | "et"; pdf_template: "standard" | "compact" }
export async function updateInvoicePdfOptions(projectID: string, companyID: string, invoiceID: string, options: InvoicePdfOptions) {
  return requestJson<InvoicePdfOptions>(`${billingPath(projectID)}/invoices/${encodeURIComponent(invoiceID)}/pdf-options`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(options),
  }, companyID)
}
export type BookInvoiceAttachmentKind = "sale" | "purchase" | "credit" | "prepayment"
export type InvoiceDelivery = {
  id: string; recipient: string; reply_to: string | null; subject: string; message: string; filename: string
  sender_name: string | null; status: "queued" | "sending" | "sent" | "failed" | "unknown"
  error_code: string | null; created_at: string; started_at: string | null; finished_at: string | null
  retry_of_id: string | null; can_retry: boolean
}
export type InvoiceDeliveryInput = { request_key: string; recipient: string; reply_to?: string; subject: string; message: string }
export type InvoiceDeliveryList = { items: InvoiceDelivery[]; total: number; default_recipient: string | null; default_reply_to: string | null }
const invoiceDeliveryPath = (projectID: string, invoiceID: string) => `${billingPath(projectID)}/invoices/${encodeURIComponent(invoiceID)}/deliveries`
export function listInvoiceDeliveries(projectID: string, companyID: string, invoiceID: string, offset = 0) {
  return boundedRead<InvoiceDeliveryList>(`${invoiceDeliveryPath(projectID, invoiceID)}?limit=25&offset=${offset}`, companyID)
}
export async function sendInvoiceEmail(projectID: string, companyID: string, invoiceID: string, input: InvoiceDeliveryInput) {
  return requestJson<InvoiceDelivery>(invoiceDeliveryPath(projectID, invoiceID), {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function retryInvoiceEmail(projectID: string, companyID: string, invoiceID: string, deliveryID: string,
  input: { request_key: string; acknowledge_possible_duplicate: boolean }) {
  return requestJson<InvoiceDelivery>(`${invoiceDeliveryPath(projectID, invoiceID)}/${encodeURIComponent(deliveryID)}/retry`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function downloadInvoiceDeliveryPdf(projectID: string, companyID: string, invoiceID: string, deliveryID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}${invoiceDeliveryPath(projectID, invoiceID)}/${encodeURIComponent(deliveryID)}/pdf`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(), filename: downloadFilename(response.headers.get("Content-Disposition")) || "andmera-invoice.pdf" }
}
export type BookInvoiceAttachment = {
  id: string; filename: string; content_type: string; size_bytes: number; sha256: string
  created_at: string; uploaded_by: string
}
const attachmentPath = (projectID: string, kind: BookInvoiceAttachmentKind, documentID: string) =>
  `${billingPath(projectID)}/attachments/${kind}/${encodeURIComponent(documentID)}`
export async function listBookInvoiceAttachments(projectID: string, companyID: string,
  kind: BookInvoiceAttachmentKind, documentID: string) {
  return boundedRead<BookInvoiceAttachment[]>(attachmentPath(projectID, kind, documentID), companyID)
}
export async function uploadBookInvoiceAttachment(projectID: string, companyID: string,
  kind: BookInvoiceAttachmentKind, documentID: string, file: File) {
  const form = new FormData()
  form.append("file", file)
  return requestJson<BookInvoiceAttachment>(attachmentPath(projectID, kind, documentID), {
    method: "POST", headers: await authHeaders(false, companyID), body: form,
  }, companyID)
}
export async function downloadBookInvoiceAttachment(projectID: string, companyID: string,
  kind: BookInvoiceAttachmentKind, documentID: string, attachmentID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}${attachmentPath(projectID, kind, documentID)}/${encodeURIComponent(attachmentID)}/content`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
  return response.blob()
}
export async function removeBookInvoiceAttachment(projectID: string, companyID: string,
  kind: BookInvoiceAttachmentKind, documentID: string, attachmentID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}${attachmentPath(projectID, kind, documentID)}/${encodeURIComponent(attachmentID)}`, {
    method: "DELETE", headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
}
export type BookPrepaymentLine = { product_id?: string | null; dimension_id?: string | null;
  description: string; unit?: string | null;
  quantity: string; unit_price: string; net: string; vat: string }
export type BookPrepaymentInvoice = {
  id: string; period_id: string; number: string; series_id: string | null; series_sequence: number | null
  invoice_date: string; due_date: string
  buyer_id: string; buyer_name: string; buyer_address: string | null; buyer_registry_code: string | null
  buyer_vat_number: string | null; buyer_country: string; reference_number: string | null
  vat_rate: string; treatment: "sale_domestic_taxable" | "sale_zero_domestic" | "sale_exempt"
  currency: "EUR"; net: string; vat: string; gross: string; status: "draft" | "confirmed"
  company_iban: string | null; company_vat_number: string | null
  issuer_name: string | null; issuer_registry_code: string | null; issuer_country: string | null; issuer_address: string | null
  issuer_identity_source: "snapshot" | "current_profile_fallback" | "pending_confirmation"
  received: string; outstanding: string; payment_status: "not_applicable" | "unpaid" | "partial" | "paid"
  receipts: { payment_id: string; advance_id: string; receipt_date: string; amount: string; refunded: string }[]
  lines: BookPrepaymentLine[]
}
export type BookPrepaymentInput = {
  number: string; series_id?: string; invoice_date: string; due_date: string; buyer_id: string; currency: "EUR"
  reference_number?: string; vat_rate: string; treatment: BookPrepaymentInvoice["treatment"]
  company_bank_account_code?: string; lines: { product_id?: string; dimension_id?: string; description: string; unit?: string;
    quantity: string; unit_price: string }[]
}
export async function listBookPrepaymentInvoices(projectID: string, companyID: string) {
  return boundedRead<BookPrepaymentInvoice[]>(`${billingPath(projectID)}/prepayment-invoices`, companyID)
}
export async function createBookPrepaymentInvoice(projectID: string, periodID: string, companyID: string,
  input: BookPrepaymentInput & { request_key: string }) {
  return requestJson<BookPrepaymentInvoice>(`${billingPeriodPath(projectID, periodID)}/prepayment-invoices`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookPrepaymentInvoice(projectID: string, companyID: string, documentID: string,
  input: BookPrepaymentInput) {
  return requestJson<BookPrepaymentInvoice>(`${billingPath(projectID)}/prepayment-invoices/${encodeURIComponent(documentID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function deleteBookPrepaymentInvoice(projectID: string, companyID: string, documentID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}${billingPath(projectID)}/prepayment-invoices/${encodeURIComponent(documentID)}`, {
    method: "DELETE", headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
}
export async function confirmBookPrepaymentInvoice(projectID: string, companyID: string, documentID: string) {
  return requestJson<BookPrepaymentInvoice>(`${billingPath(projectID)}/prepayment-invoices/${encodeURIComponent(documentID)}/confirm`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function receiveBookPrepaymentInvoice(projectID: string, paymentPeriodID: string, companyID: string,
  documentID: string, input: { request_key: string; payment_id: string; amount: string;
    liability_account_code: string; vat_account_code?: string }) {
  return requestJson<BookPrepaymentInvoice>(`${billingPeriodPath(projectID, paymentPeriodID)}/prepayment-invoices/${encodeURIComponent(documentID)}/receive`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function printableBookPrepaymentInvoice(projectID: string, companyID: string, documentID: string, locale: string) {
  const query = new URLSearchParams({ locale })
  const response = await fetch(`${coworkApiBaseUrl()}${billingPath(projectID)}/prepayment-invoices/${encodeURIComponent(documentID)}/print?${query}`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
  return response.blob()
}

export type BookOpeningItem = {
  id: string; batch_id: string; source_row: number; period_id: string; balance_date: string; account_code: string
  counterparty_id: string | null; counterparty_name: string; number: string; kind: "invoice" | "credit" | "advance"
  invoice_date: string | null; due_date: string | null; side: "debit" | "credit"; currency: string
  amount_minor: number; reporting_amount_minor: number; remaining_minor: number; reporting_remaining_minor: number
  status: string; source_file_id: string; filename: string; evidence: Record<string, unknown>
  settlements: { allocation_id: string; payment_id: string; journal_id: string; entry_date: string
    amount_minor: number; reporting_amount_minor: number; payment_amount_minor: number; details: Record<string, unknown> }[]
}
export type BookOpeningBatch = {
  id: string; period_id: string; revision: number; source_revision: string; status: "draft" | "active" | "cancelled"
  balance_date: string; account_code: string; source_file_id: string; source_extraction_id: string; filename: string
  source_total_minor: number; item_total_minor: number; ledger_minor: number | null; reconciled: boolean
  issues: { source_row?: number; reason: string }[]; items: BookOpeningItem[]
}
export function listBookOpeningBatches(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookOpeningBatch[]>(`${billingPeriodPath(projectID, periodID)}/opening-item-batches`, companyID)
}
export function listBookOpeningItems(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookOpeningItem[]>(`${billingPeriodPath(projectID, periodID)}/opening-items`, companyID)
}
export async function reviewBookOpeningBatch(projectID: string, periodID: string, companyID: string, batchID: string,
  input: { expected_revision: number; mappings: { source_row: number; counterparty_id: string }[] }) {
  return requestJson<BookOpeningBatch>(`${billingPeriodPath(projectID, periodID)}/opening-item-batches/${encodeURIComponent(batchID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function decideBookOpeningBatch(projectID: string, periodID: string, companyID: string, batchID: string,
  decision: { action: "confirm"; source_revision: string } | { action: "cancel"; expected_revision: number; reason: string }) {
  const { action, ...input } = decision
  return requestJson<BookOpeningBatch>(`${billingPeriodPath(projectID, periodID)}/opening-item-batches/${encodeURIComponent(batchID)}/${action}`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}

export type BookCurrency = "EUR" | "USD" | "GBP" | "CHF" | "SEK" | "NOK" | "DKK" | "PLN" | "CAD" | "AUD"
export type BookExchangeRate = { eur_per_unit: string; rate_date: string; source: string }
export type BookPaymentAllocationInput = {
  request_key: string; amount: string; entry_date?: string; payment_amount?: string; conversion_evidence?: string
  fx_gain_account_code?: string; fx_loss_account_code?: string
} & ({ invoice_id: string; opening_item_id?: never } | { opening_item_id: string; invoice_id?: never })
export type BookPaymentAllocation = {
  id: string; invoice_id: string | null; opening_item_id?: string | null; invoice_number: string; amount: string; journal_id: string
  payment_amount?: string; reporting_amount?: string
  settlement?: { currency: string; payment_currency: string; realized_gain_minor: number; conversion_evidence: string | null } | null
  reversed: boolean; reversal_journal_id: string | null
  period_id?: string | null; entry_date?: string | null; reversal_date?: string | null
}
export type BookPayment = {
  id: string; period_id: string; direction: "incoming" | "outgoing"; payment_date: string; amount: string
  currency: BookCurrency; exchange_rate?: BookExchangeRate | null; reporting_amount?: string | null
  bank_account_code: string; clearing_account_code: string | null; counterparty_id: string | null
  requires_review?: boolean; review_note?: string | null
  reference_number: string | null; description: string; status: "draft" | "confirmed" | "cancelled" | "reversed"
  reconciliation_status: "draft" | "unreconciled" | "partial" | "reconciled" | "cancelled" | "reversed"
  can_edit?: boolean; can_delete?: boolean
  account_allocations?: { id: string; account_code: string; amount: string; peer_payment_id: string | null; journal_id: string; reversed: boolean }[]
  bank_correction?: { journal_id: string; replacement_payment_id: string | null } | null
  allocated: string; unallocated: string; wage_allocated: string; wage_allocation_count: number
  advance_allocated: string; advance_count: number
  supplier_advance_allocated: string; supplier_advance_count: number
  advance_refund_allocated: string; advance_refund_count: number
  journal_id: string | null; allocations: BookPaymentAllocation[]
  refunds: { id: string; credit_id: string; invoice_id: string; credit_number: string; amount: string; journal_id: string }[]
}
export type BookPaymentInput = {
  direction: "incoming" | "outgoing"; payment_date: string; amount: string; currency: BookCurrency; exchange_rate?: BookExchangeRate | null
  bank_account_code: string; clearing_account_code?: string | null; counterparty_id?: string
  reference_number?: string; description: string
}
export async function postBankSettlement(projectID: string, periodID: string, companyID: string, recordID: string,
  action: "account-allocation" | "transfer-match" | "counterparty" | "reverse-bank-movement" | "reverse-account-allocation", input: Record<string, unknown>) {
  const path = action === "reverse-account-allocation" ? `/account-allocations/${encodeURIComponent(recordID)}/reverse` : `/payments/${encodeURIComponent(recordID)}/${action}`
  return requestJson<BookPayment>(`${billingPeriodPath(projectID, periodID)}${path}`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export function listBankSettlementCandidates(projectID: string, companyID: string, paymentID: string, kind: "transfer" | "replacement") {
  return boundedRead<BookPayment[]>(`${billingPath(projectID)}/payments/${encodeURIComponent(paymentID)}/settlement-candidates?kind=${kind}`, companyID)
}
export async function listBookPayments(projectID: string, periodID: string, companyID: string, includeCancelled = false, options: AccountingReadOptions = {}) {
  const params = accountingReadParams(options)
  if (includeCancelled) params.set("include_cancelled", "true")
  return boundedRead<BookPayment[]>(`${billingPeriodPath(projectID, options.allDates ? "ALL" : periodID)}/payments?${params}`, companyID)
}
export async function cancelBookPayment(projectID: string, periodID: string, companyID: string, paymentID: string) {
  return requestJson<BookPayment>(`${billingPeriodPath(projectID, periodID)}/payments/${encodeURIComponent(paymentID)}/cancel`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function createBookPayment(projectID: string, periodID: string, companyID: string, input: BookPaymentInput & { request_key: string }) {
  return requestJson<BookPayment>(`${billingPeriodPath(projectID, periodID)}/payments`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBookPayment(projectID: string, periodID: string, companyID: string, paymentID: string, input: BookPaymentInput) {
  return requestJson<BookPayment>(`${billingPeriodPath(projectID, periodID)}/payments/${encodeURIComponent(paymentID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function confirmBookPayment(projectID: string, periodID: string, companyID: string, paymentID: string) {
  return requestJson<BookPayment>(`${billingPeriodPath(projectID, periodID)}/payments/${encodeURIComponent(paymentID)}/confirm`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function allocateBookPayment(projectID: string, periodID: string, companyID: string, paymentID: string,
  input: BookPaymentAllocationInput) {
  return requestJson<BookPayment>(`${billingPeriodPath(projectID, periodID)}/payments/${encodeURIComponent(paymentID)}/allocate`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function reverseBookAllocation(projectID: string, periodID: string, companyID: string, allocationID: string,
  input: { entry_date: string; reason: string }) {
  return requestJson<BookPayment>(`${billingPeriodPath(projectID, periodID)}/allocations/${encodeURIComponent(allocationID)}/reverse`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export type BookAutoMatchPreview = {
  candidates: { payment_id: string; invoice_id: string | null; opening_item_id?: string; record_kind?: "opening_item"; currency?: string; invoice_number: string; payment_date: string;
    amount: string; reference_number: string }[]
  eligible_payments: number; skipped_count: number
}
export type BookPartialMatchPreview = { candidates: {
  payment_id: string; invoice_id: string | null; opening_item_id?: string; record_kind?: "opening_item"; currency?: string; invoice_number: string; invoice_period_id: string
  payment_date: string; reference_number: string; payment_unallocated: string
  invoice_outstanding: string; proposed_amount: string }[]
  eligible_payments: number; skipped_count: number
  skipped: { payment_id: string; reason: string }[] }
export async function previewBookPartialMatches(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookPartialMatchPreview>(`${billingPeriodPath(projectID, periodID)}/partial-match-preview`, companyID)
}
export type BookAutoMatchResult = { matched_count: number; skipped_count: number; matches: BookAutoMatchPreview["candidates"] }
export async function previewBookAutoMatches(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookAutoMatchPreview>(`${billingPeriodPath(projectID, periodID)}/auto-match-preview`, companyID)
}
export async function applyBookAutoMatches(projectID: string, periodID: string, companyID: string) {
  return requestJson<BookAutoMatchResult>(`${billingPeriodPath(projectID, periodID)}/auto-match`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export type RefundDueCredit = BillingCreditNote & { invoice_refund_due: string; counterparty_id: string; counterparty_name: string }
export async function listRefundDueCredits(projectID: string, companyID: string) {
  return boundedRead<RefundDueCredit[]>(`${billingPath(projectID)}/refund-due-credits`, companyID)
}
export async function matchBookCreditRefund(projectID: string, periodID: string, companyID: string, paymentID: string,
  input: { request_key: string; credit_id: string; amount: string }) {
  return requestJson<BookPayment>(`${billingPeriodPath(projectID, periodID)}/payments/${encodeURIComponent(paymentID)}/refund`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export type BookCustomerAdvance = {
  id: string; project_id: string; period_id: string; payment_id: string; buyer_id: string; buyer_name: string
  received_date: string; amount: string; net: string; vat: string; vat_rate: string
  treatment: "sale_domestic_taxable" | "sale_zero_domestic" | "sale_exempt"
  liability_account_code: string; vat_account_code: string | null; journal_id: string
  applied: string; refunded: string; remaining: string; status: "open" | "partial" | "applied" | "refunded" | "closed"
  applications: { id: string; invoice_id: string; invoice_number: string; application_date: string
    amount: string; net: string; vat: string; journal_id: string; reversed: boolean;
    reversal_journal_id: string | null }[]
  refunds: { id: string; payment_id: string; refund_date: string; amount: string;
    net: string; vat: string; journal_id: string }[]
}
export async function listBookCustomerAdvances(projectID: string, companyID: string) {
  return boundedRead<BookCustomerAdvance[]>(`${billingPath(projectID)}/customer-advances`, companyID)
}
export async function receiveBookCustomerAdvance(projectID: string, paymentPeriodID: string, companyID: string,
  paymentID: string, input: { request_key: string; buyer_id: string; amount: string; vat_rate: string;
    treatment: BookCustomerAdvance["treatment"]; liability_account_code: string; vat_account_code?: string }) {
  return requestJson<BookCustomerAdvance>(`${billingPeriodPath(projectID, paymentPeriodID)}/payments/${encodeURIComponent(paymentID)}/customer-advances`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function applyBookCustomerAdvance(projectID: string, applicationPeriodID: string, companyID: string,
  advanceID: string, input: { request_key: string; invoice_id: string; application_date: string; amount: string }) {
  return requestJson<BookCustomerAdvance>(`${billingPeriodPath(projectID, applicationPeriodID)}/customer-advances/${encodeURIComponent(advanceID)}/apply`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function refundBookCustomerAdvance(projectID: string, paymentPeriodID: string, companyID: string,
  paymentID: string, input: { request_key: string; advance_id: string; amount: string }) {
  return requestJson<BookCustomerAdvance>(`${billingPeriodPath(projectID, paymentPeriodID)}/payments/${encodeURIComponent(paymentID)}/customer-advance-refunds`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function reverseBookCustomerAdvanceApplication(projectID: string, correctionPeriodID: string,
  companyID: string, applicationID: string, input: { entry_date: string; reason: string }) {
  return requestJson<BookCustomerAdvance>(`${billingPeriodPath(projectID, correctionPeriodID)}/customer-advance-applications/${encodeURIComponent(applicationID)}/reverse`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export type BookSupplierAdvance = {
  id: string; project_id: string; period_id: string; payment_id: string; supplier_id: string; supplier_name: string
  paid_date: string; amount: string; asset_account_code: string; journal_id: string
  applied: string; refunded: string; remaining: string; status: "open" | "partial" | "applied" | "refunded" | "closed"
  applications: { id: string; invoice_id: string; invoice_number: string; application_date: string
    amount: string; journal_id: string; reversed: boolean; reversal_journal_id: string | null }[]
  refunds: { id: string; payment_id: string; refund_date: string; amount: string; journal_id: string }[]
}
export async function listBookSupplierAdvances(projectID: string, companyID: string) {
  return boundedRead<BookSupplierAdvance[]>(`${billingPath(projectID)}/supplier-advances`, companyID)
}
export async function recordBookSupplierAdvance(projectID: string, paymentPeriodID: string, companyID: string,
  paymentID: string, input: { request_key: string; supplier_id: string; amount: string; asset_account_code: string }) {
  return requestJson<BookSupplierAdvance>(`${billingPeriodPath(projectID, paymentPeriodID)}/payments/${encodeURIComponent(paymentID)}/supplier-advances`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function applyBookSupplierAdvance(projectID: string, applicationPeriodID: string, companyID: string,
  advanceID: string, input: { request_key: string; invoice_id: string; application_date: string; amount: string }) {
  return requestJson<BookSupplierAdvance>(`${billingPeriodPath(projectID, applicationPeriodID)}/supplier-advances/${encodeURIComponent(advanceID)}/apply`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function refundBookSupplierAdvance(projectID: string, paymentPeriodID: string, companyID: string,
  paymentID: string, input: { request_key: string; advance_id: string; amount: string }) {
  return requestJson<BookSupplierAdvance>(`${billingPeriodPath(projectID, paymentPeriodID)}/payments/${encodeURIComponent(paymentID)}/supplier-advance-refunds`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function reverseBookSupplierAdvanceApplication(projectID: string, correctionPeriodID: string,
  companyID: string, applicationID: string, input: { entry_date: string; reason: string }) {
  return requestJson<BookSupplierAdvance>(`${billingPeriodPath(projectID, correctionPeriodID)}/supplier-advance-applications/${encodeURIComponent(applicationID)}/reverse`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export type BookBankStatement = {
  id: string; bank_account_code: string; iban: string; filename: string; start_date: string; end_date: string
  period_id: string; imported_count: number; duplicate_count: number; sha256: string; linked_count?: number
}
export type BankStatementRow = { source_ref: string; date: string; direction: "incoming" | "outgoing"; amount_minor: number;
  description: string; counterparty_name?: string; counterparty_iban?: string; payment_id?: string; period_id?: string; journal_id?: string }
export type BankStatementPreview = Partial<BookBankStatement> & {
  existing?: boolean; preview_token?: string; balance_check: string; unspecified_status_count: number;
  opening_minor: number | null; closing_minor: number | null; incoming_minor: number; outgoing_minor: number;
  ledger_check?: string; ledger_opening_minor?: number; ledger_closing_minor?: number; entries: BankStatementRow[];
  candidates: Record<string, { id: string; status: string; period_id: string; description: string; reference_number: string | null }[]>
}
export type BankStatementDecision = { action: "create" | "link"; payment_id?: string; reason: string }
export async function getBookBankStatement(projectID: string, companyID: string, statementID: string) {
  return boundedRead<BankStatementPreview>(`${billingPath(projectID)}/bank-statements/${encodeURIComponent(statementID)}`, companyID)
}
export async function getBankStatementDefaults(projectID: string, companyID: string, account: string) {
  return boundedRead<{ incoming_clearing_account_code?: string; outgoing_clearing_account_code?: string }>(
    `${billingPath(projectID)}/bank-statement-defaults/${encodeURIComponent(account)}`, companyID)
}
export async function downloadBookBankStatement(projectID: string, companyID: string, statementID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}${billingPath(projectID)}/bank-statements/${encodeURIComponent(statementID)}/source`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(), filename: downloadFilename(response.headers.get("Content-Disposition")) || "statement.xml" }
}
export async function listBookBankStatements(projectID: string, companyID: string) {
  return boundedRead<BookBankStatement[]>(`${billingPath(projectID)}/bank-statements`, companyID)
}
export type BookStatementDocument = {
  id: string; file_id: string; document_index: number; filename: string; content_type: string; import_id: string
  period_id: string | null; status: "processing" | "review" | "draft" | "posted"; import_status: string
  extraction_status: string | null; bank: string; iban: string; currency: string
  start_date: string | null; end_date: string | null; transaction_count: number | null
  opening_minor?: number | null; closing_minor?: number | null; incoming_minor?: number | null; outgoing_minor?: number | null
  balance_check: string; running_balance_check: string; totals_check: string
  linked_count: number | null; draft_count: number | null; posted_count: number | null; unlinked_count: number | null; review_count: number | null
}
export type BookStatementDocumentDetail = BookStatementDocument & {
  opening_minor: number | null; closing_minor: number | null; incoming_minor: number | null; outgoing_minor: number | null
  rows: {
  key: string; date: string | null; signed_minor: number | null; description: string; counterparty_name: string; bank_reference: string
  state: "review" | "draft" | "posted"
  links: { id: string; period_id: string; status: string; matches_source: boolean; posted: boolean; requires_review: boolean }[]
}[] }
export function listBookStatementDocuments(projectID: string, companyID: string, periodID: string, offset = 0) {
  const query = new URLSearchParams({ period_id: periodID, offset: String(offset), limit: "25" })
  return boundedRead<{ items: BookStatementDocument[]; total: number; offset: number; limit: number }>(
    `${billingPath(projectID)}/bank-statement-documents?${query}`, companyID)
}
export function getBookStatementDocument(projectID: string, companyID: string, periodID: string, fileID: string, index: number) {
  return boundedRead<BookStatementDocumentDetail>(`${billingPath(projectID)}/bank-statement-documents/${encodeURIComponent(fileID)}/${index}?period_id=${encodeURIComponent(periodID)}`, companyID)
}
export async function uploadBookBankStatement(projectID: string, periodID: string, companyID: string, input: {
  bank_account_code: string; incoming_clearing_account_code: string; outgoing_clearing_account_code: string; file: File
  preview_token?: string; decisions?: Record<string, BankStatementDecision>
}, preview = false) {
  const form = new FormData()
  form.append("bank_account_code", input.bank_account_code)
  form.append("incoming_clearing_account_code", input.incoming_clearing_account_code)
  form.append("outgoing_clearing_account_code", input.outgoing_clearing_account_code)
  form.append("file", input.file)
  if (input.preview_token) form.append("preview_token", input.preview_token)
  if (input.decisions) form.append("decisions", JSON.stringify(input.decisions))
  return requestJson<BookBankStatement & BankStatementPreview>(`${billingPeriodPath(projectID, periodID)}/bank-statements${preview ? "/preview" : ""}`, {
    method: "POST", headers: await authHeaders(false, companyID), body: form,
  }, companyID)
}

export type BillingCreditLine = { invoice_line_id: string; description: string; quantity: string; net: string; vat: string }
export type BillingCreditNote = {
  id: string; invoice_id: string; invoice_number: string; invoice_type: "sale" | "purchase"
  period_id: string; number: string; credit_date: string; status: "draft" | "confirmed" | "cancelled"
  net: string; vat: string; gross: string; journal_id: string | null; lines: BillingCreditLine[]
  refunded: string; refundable_remaining: string
}
export type BillingCreditInput = { invoice_id: string; number: string; credit_date: string;
  lines: { invoice_line_id: string; quantity: string }[] }
export async function listBillingCredits(projectID: string, periodID: string, companyID: string, includeCancelled = false) {
  return boundedRead<BillingCreditNote[]>(`${billingPeriodPath(projectID, periodID)}/credits${includeCancelled ? "?include_cancelled=true" : ""}`, companyID)
}
export async function cancelBillingCredit(projectID: string, periodID: string, companyID: string, creditID: string) {
  return requestJson<BillingCreditNote>(`${billingPeriodPath(projectID, periodID)}/credits/${encodeURIComponent(creditID)}/cancel`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}
export async function downloadConfirmedBillingCreditPdf(projectID: string, companyID: string, creditID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}${billingPath(projectID)}/credits/${encodeURIComponent(creditID)}/pdf`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) await handleAuthFailure(response)
  return { blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "andmera-credit-note.pdf" }
}
export async function createBillingCredit(projectID: string, periodID: string, companyID: string, input: BillingCreditInput & { request_key: string }) {
  return requestJson<BillingCreditNote>(`${billingPeriodPath(projectID, periodID)}/credits`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function updateBillingCredit(projectID: string, periodID: string, companyID: string, creditID: string,
  input: Omit<BillingCreditInput, "invoice_id">) {
  return requestJson<BillingCreditNote>(`${billingPeriodPath(projectID, periodID)}/credits/${encodeURIComponent(creditID)}`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify(input),
  }, companyID)
}
export async function confirmBillingCredit(projectID: string, periodID: string, companyID: string, creditID: string) {
  return requestJson<BillingCreditNote>(`${billingPeriodPath(projectID, periodID)}/credits/${encodeURIComponent(creditID)}/confirm`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}

export async function createAccountingExportPackage(workflowID: string) {
  return requestJson<CoworkAccountingExportPackage>(`/api/v1/accounting/workflows/${workflowID}/export-packages`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ package_type: "ledger_export", formats: ["csv", "xlsx", "xml", "json", "pdf"] }),
  })
}

export async function updateAccountingExportManualStatus(packageID: string, manualSubmissionStatus: string) {
  return requestJson<CoworkAccountingExportPackage>(
    `/api/v1/accounting/export-packages/${packageID}/manual-submission`,
    {
      method: "PATCH",
      headers: await jsonHeaders(),
      body: JSON.stringify({ manual_submission_status: manualSubmissionStatus }),
    },
  )
}

export async function downloadAccountingExportFile(fileID: string) {
  return requestJson<CoworkAccountingExportFileDownload>(`/api/v1/accounting/export-files/${fileID}`)
}

export async function deleteAccountingExportFile(fileID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/v1/accounting/export-files/${fileID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) await handleAuthFailure(response)
}

export async function listAccountingTaxReports(workflowID: string) {
  return requestJson<CoworkAccountingTaxReport[]>(`/api/v1/accounting/workflows/${workflowID}/tax-reports`).catch(
    () => [],
  )
}

export async function createAccountingTaxReport(
  workflowID: string,
  body: { report_type?: string; jurisdiction?: string; reporting_period?: string | null } = {},
) {
  return requestJson<CoworkAccountingTaxReport>(`/api/v1/accounting/workflows/${workflowID}/tax-reports`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({
      report_type: body.report_type ?? "kmd",
      jurisdiction: body.jurisdiction ?? "EE",
      reporting_period: body.reporting_period ?? null,
    }),
  })
}

export async function approveAccountingTaxReport(reportID: string, comment?: string) {
  return requestJson<CoworkAccountingTaxReport>(`/api/v1/accounting/tax-reports/${reportID}/approve`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ comment }),
  })
}

export async function rejectAccountingTaxReport(reportID: string, comment?: string) {
  return requestJson<CoworkAccountingTaxReport>(`/api/v1/accounting/tax-reports/${reportID}/reject`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ comment }),
  })
}

export async function deleteAccountingTaxReport(reportID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/v1/accounting/tax-reports/${reportID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) await handleAuthFailure(response)
}

export async function listAccountingTaxSources(reportType = "kmd", jurisdiction = "EE") {
  const params = new URLSearchParams({ report_type: reportType, jurisdiction })
  return requestJson<CoworkAccountingTaxSource[]>(`/api/v1/accounting/tax-sources?${params.toString()}`).catch(() => [])
}

export async function createProject(name: string, companyID?: string) {
  return requestJson<CoworkProject>("/api/projects", {
    method: "POST",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify({ name }),
  })
}

export async function updateProject(projectID: string, name: string, companyID?: string) {
  return requestJson<CoworkProject>(`/api/projects/${projectID}`, {
    method: "PATCH",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify({ name }),
  })
}

export async function deleteProject(projectID: string, companyID?: string) {
  const headers = await authHeaders()
  if (companyID) headers["X-Active-Company-Id"] = companyID
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}`, {
    method: "DELETE",
    headers,
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listSessions(projectID: string, workflowID?: string, companyID?: string) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkSession[]>(`/api/projects/${projectID}/sessions${query}`, undefined, companyID)
}

export async function listPortfolioChats(options?: { entityID?: string; projectID?: string }) {
  const query = new URLSearchParams()
  if (options?.entityID) query.set("entity_id", options.entityID)
  if (options?.projectID) query.set("project_id", options.projectID)
  return requestJson<CoworkPortfolioChat[]>(`/api/chats?${query.toString()}`)
}

export async function listWorkflows(projectID: string) {
  return requestJson<CoworkWorkflow[]>(`/api/projects/${projectID}/workflows`)
}

export async function listWorkflowEntries(options?: { entityID?: string; projectID?: string }) {
  const query = new URLSearchParams({ include_periods: "true" })
  if (options?.entityID) query.set("entity_id", options.entityID)
  if (options?.projectID) query.set("project_id", options.projectID)
  return requestJson<CoworkWorkflowEntry[]>(`/api/workflows?${query.toString()}`)
}

export async function listPortfolioWorkflows(options?: { entityID?: string; projectID?: string }) {
  const query = new URLSearchParams()
  if (options?.entityID) query.set("entity_id", options.entityID)
  if (options?.projectID) query.set("project_id", options.projectID)
  return requestJson<CoworkPortfolioWorkflow[]>(`/api/workflows?${query.toString()}`)
}

export async function getReportRegistry() {
  return requestJson<CoworkReportRegistry>("/api/report-templates")
}

export async function listPeriods(projectID: string, companyID?: string) {
  return boundedRead<CoworkPeriod[]>(`/api/projects/${projectID}/periods`, companyID)
}

export async function createPeriod(
  projectID: string,
  input: {
    name: string
    granularity: CoworkPeriod["granularity"]
    start_date?: string
    end_date?: string
    fiscal_year?: number
    is_current?: boolean
  },
  companyID?: string,
) {
  return requestJson<CoworkPeriod>(`/api/projects/${projectID}/periods`, {
    method: "POST",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify(input),
  })
}

export async function updatePeriod(
  projectID: string,
  periodID: string,
  input: Partial<
    Pick<
      CoworkPeriod,
      "name" | "granularity" | "start_date" | "end_date" | "fiscal_year" | "period_status" | "is_current" | "is_locked"
    >
  >,
  companyID?: string,
) {
  return requestJson<CoworkPeriod>(`/api/projects/${projectID}/periods/${periodID}`, {
    method: "PATCH",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify(input),
  })
}

export async function closePeriod(projectID: string, periodID: string, companyID?: string) {
  return requestJson<CoworkPeriod>(
    `/api/projects/${projectID}/periods/${periodID}/close`,
    { method: "POST" },
    companyID,
  )
}

export async function reopenPeriod(projectID: string, periodID: string, companyID?: string) {
  return requestJson<CoworkPeriod>(
    `/api/projects/${projectID}/periods/${periodID}/reopen`,
    { method: "POST" },
    companyID,
  )
}

export async function createWorkflow(projectID: string, input: CoworkWorkflowInput, companyID?: string) {
  return requestJson<CoworkWorkflow>(`/api/projects/${projectID}/workflows`, {
    method: "POST",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify(input),
  })
}

export async function updateWorkflow(
  projectID: string,
  workflowID: string,
  input: Partial<CoworkWorkflowInput> & { status?: "active" | "disabled" },
  companyID?: string,
) {
  return requestJson<CoworkWorkflow>(`/api/projects/${projectID}/workflows/${workflowID}`, {
    method: "PATCH",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify(input),
  })
}

export async function listWorkflowRuns(projectID: string, workflowID: string, companyID?: string) {
  return requestJson<CoworkWorkflowRun[]>(`/api/projects/${projectID}/workflows/${workflowID}/runs`, {
    headers: await authHeaders(false, companyID),
  })
}

export async function runWorkflow(
  projectID: string,
  workflowID: string,
  periodID?: string,
  options?: { requestID?: string; rerunOfRunID?: string; companyID?: string; applicationLocale?: string },
) {
  return requestJson<CoworkWorkflowRun>(`/api/projects/${projectID}/workflows/${workflowID}/runs`, {
    method: "POST",
    headers: await jsonHeaders(options?.companyID),
    body: JSON.stringify({
      period_id: periodID,
      request_id: options?.requestID,
      rerun_of_run_id: options?.rerunOfRunID,
      application_locale: options?.applicationLocale,
    }),
  })
}

export async function getPeriodWorkflowPipeline(projectID: string, periodID: string, companyID: string) {
  return requestJson<CoworkPeriodWorkflowPipeline>(
    `/api/projects/${encodeURIComponent(projectID)}/periods/${encodeURIComponent(periodID)}/pipeline`,
    undefined, companyID, { preserveAuthOnForbidden: true },
  )
}

export async function getWorkflowRunPipeline(projectID: string, workflowID: string, runID: string, companyID: string) {
  return requestJson<CoworkWorkflowRunPipeline>(
    `/api/projects/${encodeURIComponent(projectID)}/workflows/${encodeURIComponent(workflowID)}/runs/${encodeURIComponent(runID)}/pipeline`,
    undefined, companyID, { preserveAuthOnForbidden: true },
  )
}

export async function getWorkflowRun(projectID: string, workflowID: string, runID: string, companyID?: string) {
  return requestJson<CoworkWorkflowRun>(`/api/projects/${projectID}/workflows/${workflowID}/runs/${runID}`, {
    headers: await authHeaders(false, companyID),
  })
}

export async function downloadWorkflowRunArtifact(
  projectID: string,
  workflowID: string,
  runID: string,
  artifactID: string,
  companyID?: string,
) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/projects/${projectID}/workflows/${workflowID}/runs/${runID}/artifacts/${artifactID}/download`,
    { headers: await authHeaders(false, companyID) },
  )
  if (!response.ok) await handleAuthFailure(response)
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export async function deleteWorkflow(projectID: string, workflowID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/workflows/${workflowID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function createSession(
  projectID: string,
  title: string,
  periodID?: string,
  metadata: Record<string, string | number | boolean> = {},
  agentID?: string,
  scope?: { type: "workspace" | "company" | "project"; entityID?: string },
  companyID?: string,
  creationRequestID?: string,
) {
  return requestJson<CoworkSession>(`/api/projects/${projectID}/sessions`, {
    method: "POST",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify({
      title,
      period_id: periodID,
      agent_id: agentID,
      scope_type: scope?.type || "project",
      scope_entity_id: scope?.entityID,
      metadata,
      creation_request_id: creationRequestID,
    }),
  })
}

export async function updateSessionAccountingContext(
  projectID: string,
  sessionID: string,
  context: CoworkAccountingContextUpdate,
) {
  return requestJson<CoworkSession>(`/api/projects/${projectID}/sessions/${sessionID}/accounting-context`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(context),
  })
}

export async function syncCoworkSessionAgent(remoteSessionID: string, runtimeAgent: string) {
  const context = await requestJson<CoworkSessionContext>(`/api/sessions/remote/${remoteSessionID}/context`)
  const capability = context.session_metadata?.report_capability as { runtime_agent?: string } | undefined
  const agent = context.session_metadata?.agent as { runtime_agent_name?: string } | undefined
  if ((capability?.runtime_agent || agent?.runtime_agent_name) === runtimeAgent) return
  await requestJson<CoworkSession>(`/api/projects/${context.project_id}/sessions/${context.session_id}/agent`, {
    method: "PATCH",
    headers: await jsonHeaders(context.company_id),
    body: JSON.stringify({ runtime_agent: runtimeAgent }),
  }, context.company_id)
}

export function isPeriodicalProject(project: CoworkProject | undefined) {
  return (
    project?.project_metadata?.work_organization === "periodical" || project?.project_metadata?.period_driven === true
  )
}

export function isAccountingProject(project: CoworkProject | undefined) {
  return (
    project?.project_metadata?.accounting_enabled === true || project?.project_metadata?.project_type === "accounting"
  )
}

export async function ensureRemoteSession(projectID: string, sessionID: string, companyID?: string) {
  return requestJson<CoworkSession>(`/api/projects/${projectID}/sessions/${sessionID}/remote`, {
    method: "POST",
    headers: await jsonHeaders(companyID),
  })
}

export async function syncRemoteSessionMessages(remoteSessionID: string) {
  return requestJson<{ synced: number; title: string; title_updated: boolean }>(
    `/api/sessions/remote/${remoteSessionID}/sync`,
    {
      method: "POST",
      headers: await jsonHeaders(),
    },
  )
}

export async function deleteCoworkSession(projectID: string, sessionID: string, companyID?: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/sessions/${sessionID}`, {
    method: "DELETE",
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function renameCoworkSession(projectID: string, sessionID: string, title: string, companyID?: string) {
  return requestJson<CoworkSession>(`/api/projects/${projectID}/sessions/${sessionID}/title`, {
    method: "PATCH",
    headers: await jsonHeaders(companyID),
    body: JSON.stringify({ title }),
  }, companyID)
}

export async function createIntegrationConnectSession(providerKey: string) {
  return requestJson<CoworkConnectSession>(`/api/integrations/connect-session`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ allowed_integrations: [providerKey] }),
  })
}

export async function listIntegrationConnections(providerKey?: string) {
  const query = providerKey ? `?provider_key=${encodeURIComponent(providerKey)}` : ""
  return requestJson<{ connections: CoworkIntegrationConnection[] }>(`/api/integrations/connections${query}`).catch(
    () => ({ connections: [] }),
  )
}

export async function deleteIntegrationConnection(providerKey: string, connectionID: string) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/integrations/connections?provider_key=${encodeURIComponent(providerKey)}&connection_id=${encodeURIComponent(connectionID)}`,
    {
      method: "DELETE",
      headers: await authHeaders(),
    },
  )
  if (!response.ok) throw new Error(await response.text())
}

export async function listIntegrationFiles(input: {
  providerKey: string
  connectionID: string
  query?: string
  cursor?: string
  path?: string
  folderID?: string
}) {
  const params = new URLSearchParams({ provider_key: input.providerKey, connection_id: input.connectionID })
  if (input.query) params.set("query", input.query)
  if (input.cursor) params.set("cursor", input.cursor)
  if (input.path) params.set("path", input.path)
  if (input.folderID) params.set("folder_id", input.folderID)
  return requestJson<{ items: CoworkCloudFile[]; next_cursor?: string | null }>(
    `/api/integrations/files?${params.toString()}`,
  )
}

export async function importIntegrationFiles(input: {
  projectID: string
  workflowID?: string | null
  sessionID?: string | null
  providerKey: string
  connectionID: string
  files: CoworkCloudFile[]
}) {
  return requestJson<{
    imported_count: number
    skipped_count: number
    results: Array<{
      external_file_id: string
      status: string
      file_id?: string | null
      file_name?: string | null
      reason?: string | null
    }>
  }>(`/api/integrations/files/import`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({
      project_id: input.projectID,
      workflow_id: input.workflowID,
      session_id: input.sessionID,
      provider_key: input.providerKey,
      connection_id: input.connectionID,
      files: input.files.map((file) => ({
        external_file_id: file.id,
        name: file.name,
        mime_type: file.mime_type,
        size: file.size,
        path: file.path,
        version: file.version,
      })),
    }),
  })
}

export function buildIntegrationConnectUrl(session: CoworkConnectSession) {
  try {
    const source = new URL(session.connect_link)
    const sessionToken = source.searchParams.get("session_token")
    if (!sessionToken) return session.connect_link
    const connectUiBase = import.meta.env.VITE_NANGO_CONNECT_UI_BASE_URL || "https://connect.integrations.andmera.com"
    const target = new URL(connectUiBase)
    const apiURL = source.hostname.startsWith("connect.")
      ? import.meta.env.VITE_NANGO_API_URL || "https://integrations.andmera.com"
      : source.origin
    target.searchParams.set("session_token", sessionToken)
    target.searchParams.set("apiURL", apiURL)
    return target.toString()
  } catch {
    return session.connect_link
  }
}

export async function listMessages(projectID: string, sessionID: string) {
  return requestJson<CoworkMessage[]>(`/api/projects/${projectID}/sessions/${sessionID}/messages`)
}

export async function listFiles(projectID: string, workflowID?: string, companyID?: string) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkFile[]>(`/api/projects/${projectID}/files${query}`, undefined, companyID)
}

export async function listPortfolioFiles(options?: {
  entityID?: string
  projectID?: string
  periodID?: string
  companyID?: string
  signal?: AbortSignal
}) {
  const query = new URLSearchParams()
  query.set("entity_id", options?.entityID || "ALL")
  if (options?.projectID) query.set("project_id", options.projectID)
  if (options?.periodID && options.periodID !== "ALL") query.set("period_id", options.periodID)
  return requestJson<CoworkPortfolioFile[]>(`/api/files?${query.toString()}`, options?.signal ? { signal: options.signal } : undefined, options?.companyID)
}

export async function getPortfolioFileContentBlob(fileID: string, companyID?: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/files/${fileID}/content`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) throw new Error(await response.text())
  return response.blob()
}

export async function listPortfolioFileCatalog(options: {
  entityID: string
  projectID?: string
  periodID?: string
  companyID?: string
  search?: string
  sellerIDs?: string[]
  documentTypes?: string[]
  category?: string
  fileIDs?: string[]
}) {
  const query = new URLSearchParams({ entity_id: options.entityID })
  if (options.projectID) query.set("project_id", options.projectID)
  if (options.periodID) query.set("period_id", options.periodID)
  if (options.search) query.set("q", options.search)
  if (options.category) query.set("category", options.category)
  for (const id of options.sellerIDs ?? []) query.append("seller_id", id)
  for (const type of options.documentTypes ?? []) query.append("document_type", type)
  for (const id of options.fileIDs ?? []) query.append("file_id", id)
  return requestJson<CoworkFileCatalog>(`/api/files/catalog?${query}`, undefined, options.companyID)
}

export async function listFileExtractions(
  fileID: string,
  options: {
    companyID: string
    periodID?: string | null
    runID?: string
    schemaName?: string
  },
) {
  const query = new URLSearchParams()
  if (options.periodID) query.set("period_id", options.periodID)
  if (options.runID) query.set("workflow_run_id", options.runID)
  if (options.schemaName) query.set("schema_name", options.schemaName)
  return requestJson<CoworkFileExtraction[]>(
    `/api/files/${encodeURIComponent(fileID)}/extractions?${query}`,
    undefined,
    options.companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function listProjectKnowledge(projectID: string, companyID: string) {
  return requestJson<CoworkKnowledge[]>(`/api/projects/${projectID}/knowledge`, undefined, companyID)
}

export async function listCompanyKnowledge(companyID: string) {
  return requestJson<CoworkKnowledge[]>(
    `/api/companies/${encodeURIComponent(companyID)}/knowledge`,
    undefined,
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function uploadCompanyKnowledge(companyID: string, file: File) {
  const form = new FormData()
  form.append("file", file)
  return requestJson<CoworkKnowledge>(
    `/api/companies/${encodeURIComponent(companyID)}/knowledge/upload`,
    { method: "POST", headers: await authHeaders(false, companyID), body: form },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function linkCompanyKnowledge(companyID: string, fileID: string) {
  return requestJson<CoworkKnowledge>(
    `/api/companies/${encodeURIComponent(companyID)}/knowledge/link`,
    { method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ file_id: fileID }) },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function removeCompanyKnowledge(companyID: string, knowledgeID: string) {
  return requestJson<void>(
    `/api/companies/${encodeURIComponent(companyID)}/knowledge/${encodeURIComponent(knowledgeID)}`,
    { method: "DELETE" },
    companyID,
    { preserveAuthOnForbidden: true },
  )
}

export async function getCompanyKnowledgeContentBlob(companyID: string, knowledgeID: string) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/companies/${encodeURIComponent(companyID)}/knowledge/${encodeURIComponent(knowledgeID)}/content`,
    { headers: await authHeaders(false, companyID) },
  )
  if (!response.ok) await handleAuthFailure(response, undefined, true)
  return response.blob()
}

export async function uploadProjectKnowledge(projectID: string, file: File, companyID: string) {
  const form = new FormData()
  form.append("file", file)
  return requestJson<CoworkKnowledge>(`/api/projects/${projectID}/knowledge/upload`, {
    method: "POST",
    headers: await authHeaders(false, companyID),
    body: form,
  })
}

export async function linkProjectKnowledge(projectID: string, fileID: string, companyID: string) {
  return requestJson<CoworkKnowledge>(
    `/api/projects/${projectID}/knowledge/link`,
    {
      method: "POST",
      headers: await jsonHeaders(companyID),
      body: JSON.stringify({ file_id: fileID }),
    },
    companyID,
  )
}

export async function removeProjectKnowledge(projectID: string, knowledgeID: string, companyID: string) {
  return requestJson<void>(
    `/api/projects/${projectID}/knowledge/${knowledgeID}`,
    {
      method: "DELETE",
      headers: await authHeaders(false, companyID),
    },
    companyID,
  )
}

export async function getProjectKnowledgeContentBlob(projectID: string, knowledgeID: string, companyID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/knowledge/${knowledgeID}/content`, {
    headers: await authHeaders(false, companyID),
  })
  if (!response.ok) throw new Error(await response.text())
  return response.blob()
}

export async function getFile(projectID: string, fileID: string, companyID?: string) {
  return requestJson<CoworkFileDetail>(`/api/projects/${projectID}/files/${fileID}`, undefined, companyID)
}

export async function getFileContentBlob(projectID: string, fileID: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/files/${fileID}/content`, {
    headers: await authHeaders(false, company),
  })
  if (!response.ok) throw new Error(await response.text())
  return response.blob()
}

export async function deleteFile(projectID: string, fileID: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/files/${fileID}`, {
    method: "DELETE",
    headers: await authHeaders(false, company),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listProjectMembers(projectID: string) {
  return requestJson<CoworkProjectMember[]>(`/api/projects/${projectID}/members`)
}

export async function listArtifacts(projectID: string) {
  return requestJson<CoworkArtifact[]>(`/api/projects/${projectID}/artifacts`)
}

export async function getArtifact(projectID: string, artifactID: string, companyID?: string) {
  return requestJson<CoworkArtifact>(
    `/api/projects/${encodeURIComponent(projectID)}/artifacts/${encodeURIComponent(artifactID)}`,
    undefined,
    companyID,
  )
}

export async function listPortfolioReports(options?: {
  entityID?: string
  projectID?: string
  periodID?: string
  status?: string
  companyID?: string
  signal?: AbortSignal
}) {
  const query = new URLSearchParams()
  query.set("entity_id", options?.entityID || "ALL")
  if (options?.projectID) query.set("project_id", options.projectID)
  if (options?.periodID && options.periodID !== "ALL") query.set("period_id", options.periodID)
  if (options?.status) query.set("status", options.status)
  return requestJson<CoworkPortfolioReport[]>(`/api/reports?${query.toString()}`, options?.signal ? { signal: options.signal } : undefined, options?.companyID)
}

export async function downloadPortfolioReport(artifactID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/reports/${artifactID}/download`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export class ReportPdfExportError extends Error {
  constructor(readonly status: number) {
    super("Report PDF export failed")
  }
}

export async function exportPortfolioReportPdf(artifactID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/reports/${encodeURIComponent(artifactID)}/export/pdf`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new ReportPdfExportError(response.status)
  if (!response.headers.get("Content-Type")?.includes("application/pdf")) throw new ReportPdfExportError(502)
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report.pdf",
  }
}

export async function listArtifactsForWorkflow(projectID: string, workflowID?: string | null) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkArtifact[]>(`/api/projects/${projectID}/artifacts${query}`)
}

export async function listArtifactsForSession(projectID: string, sessionID: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  return requestJson<CoworkArtifact[]>(
    `/api/projects/${projectID}/artifacts?session_id=${encodeURIComponent(sessionID)}`,
    undefined,
    company,
  ).catch(() => [])
}

export async function listExtractions(projectID: string, workflowID?: string | null) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkExtraction[]>(`/api/projects/${projectID}/extractions${query}`).catch(() => [])
}

export async function listTransformations(projectID: string, workflowID?: string | null) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkTransformation[]>(`/api/projects/${projectID}/transformations${query}`).catch(() => [])
}

export async function listApprovals(projectID: string, workflowID?: string | null) {
  const query = workflowID ? `?period_id=${encodeURIComponent(workflowID)}` : ""
  return requestJson<CoworkApproval[]>(`/api/projects/${projectID}/approvals${query}`).catch(() => [])
}

export async function listPortfolioApprovals(options?: {
  entityID?: string
  projectID?: string
  periodID?: string
  status?: string
}) {
  const query = new URLSearchParams()
  if (options?.entityID) query.set("entity_id", options.entityID)
  if (options?.projectID) query.set("project_id", options.projectID)
  if (options?.periodID && options.periodID !== "ALL") query.set("period_id", options.periodID)
  if (options?.status) query.set("status", options.status)
  return requestJson<CoworkPortfolioApproval[]>(`/api/approvals?${query.toString()}`)
}

export async function requestApprovals(
  projectID: string,
  reportArtifactID: string,
  approverEmails: string[],
  message?: string,
  companyID?: string,
) {
  const company = companyID || activeCompanyID()
  return requestJson<CoworkApproval[]>(
    `/api/projects/${projectID}/approvals`,
    {
      method: "POST",
      headers: await jsonHeaders(company),
      body: JSON.stringify({ report_artifact_id: reportArtifactID, approver_emails: approverEmails, message }),
    },
    company,
    { preserveAuthOnForbidden: true },
  )
}

export async function updateApprovalReviewer(
  projectID: string,
  approvalID: string,
  approverEmail: string,
  companyID?: string,
) {
  const headers: Record<string, string> = await jsonHeaders()
  if (companyID) headers["X-Active-Company-Id"] = companyID
  return requestJson<CoworkApproval>(`/api/projects/${projectID}/approvals/${approvalID}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ approver_email: approverEmail }),
  })
}

export async function deleteApprovalReviewer(projectID: string, approvalID: string, companyID?: string) {
  const headers = await authHeaders()
  if (companyID) headers["X-Active-Company-Id"] = companyID
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/approvals/${approvalID}`, {
    method: "DELETE",
    headers,
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listReportShares(projectID: string, artifactID: string, companyID: string) {
  return requestJson<CoworkReportShare[]>(
    `/api/projects/${encodeURIComponent(projectID)}/artifacts/${encodeURIComponent(artifactID)}/shares`,
    undefined, companyID, { preserveAuthOnForbidden: true },
  )
}

export async function shareReport(projectID: string, artifactID: string, emails: string[], message: string | undefined, companyID: string) {
  return requestJson<CoworkReportShare[]>(
    `/api/projects/${encodeURIComponent(projectID)}/artifacts/${encodeURIComponent(artifactID)}/shares`,
    { method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ recipient_emails: emails, message }) },
    companyID, { preserveAuthOnForbidden: true },
  )
}

export async function getSharedReport(token: string) {
  return requestJson<CoworkSharedReport>(`/api/shared-reports/${encodeURIComponent(token)}`, { headers: {} }, undefined, { preserveAuthOnForbidden: true })
}

export async function downloadSharedReport(token: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/shared-reports/${encodeURIComponent(token)}/download`)
  if (!response.ok) throw new Error("Shared report download failed")
  return { blob: await response.blob(), filename: downloadFilename(response.headers.get("Content-Disposition")) || "report" }
}

export async function getProjectApprovalDetail(projectID: string, approvalID: string, companyID: string) {
  return requestJson<CoworkApprovalDetail>(
    `/api/projects/${encodeURIComponent(projectID)}/approvals/${encodeURIComponent(approvalID)}`,
    undefined, companyID, { preserveAuthOnForbidden: true },
  )
}

export async function addProjectApprovalComment(projectID: string, approvalID: string, comments: string, companyID: string) {
  return requestJson<CoworkApprovalDetail>(
    `/api/projects/${encodeURIComponent(projectID)}/approvals/${encodeURIComponent(approvalID)}/comments`,
    { method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ comments }) },
    companyID, { preserveAuthOnForbidden: true },
  )
}

export async function getApprovalReview(approvalID: string) {
  return requestJson<CoworkApprovalReview>(`/api/reviews/${approvalID}?include_content=false`, { headers: {} })
}

export async function downloadApprovalReviewReport(approvalID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/reviews/${approvalID}/download`)
  if (!response.ok) throw new Error(await response.text())
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export async function approveReview(approvalID: string, comments?: string) {
  return requestJson<CoworkApprovalReview>(`/api/reviews/${approvalID}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comments }),
  })
}

export async function rejectReview(approvalID: string, comments?: string) {
  return requestJson<CoworkApprovalReview>(`/api/reviews/${approvalID}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comments }),
  })
}

export async function saveReviewComments(approvalID: string, comments?: string) {
  return requestJson<CoworkApprovalReview>(`/api/reviews/${approvalID}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comments }),
  })
}

export async function downloadArtifact(projectID: string, artifactID: string, companyID?: string) {
  return downloadArtifactFile(`/api/projects/${projectID}/artifacts/${artifactID}/download`, companyID)
}

export async function updateArtifactContent(
  projectID: string,
  artifactID: string,
  blob: Blob,
  updatedAt: string,
  companyID?: string,
) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/projects/${encodeURIComponent(projectID)}/artifacts/${encodeURIComponent(artifactID)}/content`,
    {
      method: "PUT",
      headers: {
        ...(await authHeaders(false, companyID)),
        "Content-Type": blob.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "X-Artifact-Updated-At": updatedAt,
      },
      body: blob,
    },
  )
  if (!response.ok) await handleAuthFailure(response, undefined, true)
  return (await response.json()) as CoworkArtifact
}

export async function downloadArtifactFile(path: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  const baseURL = coworkApiBaseUrl()
  const pageURL = artifactDownloadPageURL(typeof window === "undefined" ? "" : window.location.href, baseURL)
  const downloadPath = artifactDownloadPath(path, pageURL, baseURL)
  if (!downloadPath) throw new Error("Unsupported artifact download path")
  const response = await fetch(`${baseURL}${downloadPath}`, {
    headers: await authHeaders(false, company),
  })
  if (!response.ok) throw new Error(await response.text())
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export async function deleteArtifact(projectID: string, artifactID: string, companyID?: string) {
  const headers = await authHeaders()
  if (companyID) headers["X-Active-Company-Id"] = companyID
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/artifacts/${artifactID}`, {
    method: "DELETE",
    headers,
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function addProjectMember(projectID: string, identifier: string, role: "viewer" | "editor") {
  const body = identifier.includes("@") ? { email: identifier, role } : { user_id: identifier, role }
  return requestJson<CoworkProjectMember>(`/api/projects/${projectID}/members`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(body),
  })
}

export async function removeProjectMember(projectID: string, userID: string) {
  const response = await fetch(`${coworkApiBaseUrl()}/api/projects/${projectID}/members/${userID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function uploadFile(
  projectID: string,
  file: File,
  periodID?: string | null,
  sessionID?: string,
  companyID?: string,
  options?: { autoImport?: boolean; importTarget?: string; locale?: string },
) {
  const form = new FormData()
  form.append("file", file)
  const query = new URLSearchParams()
  // Explicit null means project-level; omission preserves existing default-period behavior.
  if (periodID === null) query.set("project_level", "true")
  if (periodID) query.set("period_id", periodID)
  if (sessionID) query.set("session_id", sessionID)
  if (options?.autoImport) query.set("auto_import", "true")
  if (options?.importTarget) query.set("import_target", options.importTarget)
  if (options?.locale) query.set("application_locale", options.locale)
  return requestJson<CoworkFile>(`/api/projects/${projectID}/files${query.size ? `?${query.toString()}` : ""}`, {
    method: "POST",
    headers: await authHeaders(false, companyID),
    body: form,
  })
}

export type BookImportKind = "auto" | "invoice" | "counterparty" | "journal" | "opening" | "opening_items" | "account" | "bank_account" | "bank_identity" |
  "payment" | "statement" | "credit_note" | "prepayment_invoice" | "customer_advance" | "supplier_advance" | "allocation" | "refund" | "advance_application" | "advance_refund" | "product" | "invoice_series" | "dimension" | "asset"
export type BookOpeningSourceReview = {
  balance_date: string | null; account_code: string; reporting_currency: string; item_count: number;
  source_total_minor: number | null; item_total_minor: number;
  issues: { reason: string; required_period_start?: string; evidence?: string }[];
  items: { invoice_number: string | null; counterparty_name: string | null; currency: string | null;
    side: string | null; outstanding_amount: string | null; reporting_amount: string | null;
    invoice_date: string | null; due_date: string | null; item_kind: string | null; evidence: string[] }[];
}
export type BookImportItem = { id: string; kind: BookImportKind; status: "ready" | "needs_review" | "draft" | "created" | "dismissed";
  opening_review?: BookOpeningSourceReview | null;
  record_period_id?: string | null;
  matching_records?: { resource: string; record_id: string; status?: string; period_id?: string; payment_date?: string;
    amount?: string; bank_account_code?: string; reference_number?: string | null; requires_review: boolean }[];
  record_status?: "draft" | "confirmed" | "cancelled" | "posted" | "deleted" | "voided" | "active" | null;
  review_revision?: string;
  data: Record<string, unknown>; note?: string; record_id?: string; source_key?: string; evidence: string[]; request_key: string;
  source_amounts?: { net: string | null; vat: string | null; gross: string | null; currency?: string } | null;
  quality?: { issues: string[]; missing_fields: string[]; incomplete_lines: number; unresolved_references: number;
    source_gross: string | null; draft_gross: string | null } | null }
export type BookImportJob = { id: string; company_id: string; project_id: string; period_id: string | null; file_id: string; filename: string;
  target: BookImportKind; status: "queued" | "dispatching" | "running" | "applying" | "review" | "completed" | "failed" | "submission_unknown" | "cancelled";
  error_code?: string; document_summary?: CoworkFileDocumentSummary | null; items: BookImportItem[];
  extraction_status?: string | null; extraction_quality_status?: string | null;
  completeness?: { source_rows: number | null; created_drafts: number; unresolved_rows: number;
    memorandum_reviews: number; source_partial: boolean; source_warnings: boolean };
  overlapping_import_basis?: boolean; opening_schedule_review?: boolean; opening_reviews?: BookOpeningSourceReview[];
  statement_checks?: { transaction_count: number; balance_check: string; running_balance_check: string; totals_check: string;
    opening_minor: number | null; closing_minor: number | null; incoming_minor: number | null; outgoing_minor: number | null }[] }
export type BookImportAttention = "review" | "failed" | "processing"
export type BookDocumentOverview = { company_id: string; project_id: string; period_id: string
  file_count: number; review: number; failed: number; processing: number }
export type BookImportAttentionPage = BookDocumentOverview & { jobs: BookImportJob[]; total: number; offset: number; limit: number }
export function getBookDocumentOverview(projectID: string, periodID: string, companyID: string) {
  return boundedRead<BookDocumentOverview>(`/api/projects/${encodeURIComponent(projectID)}/imports/overview?period_id=${encodeURIComponent(periodID)}`, companyID)
}
export function listBookImportAttention(projectID: string, periodID: string, companyID: string, state: BookImportAttention, offset = 0) {
  const query = new URLSearchParams({ period_id: periodID, state, offset: String(offset) })
  return boundedRead<BookImportAttentionPage>(`/api/projects/${encodeURIComponent(projectID)}/imports/attention?${query}`, companyID)
}
export function listBookImports(projectID: string, companyID: string, periodID?: string) {
  return boundedRead<BookImportJob[]>(`/api/projects/${encodeURIComponent(projectID)}/imports${periodID ? `?period_id=${encodeURIComponent(periodID)}` : ""}`, companyID)
}
export function saveBookStatementReviewDrafts(projectID: string, companyID: string, jobID: string) {
  return jsonHeaders(companyID).then((headers) => requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/payment-review-drafts`, {
    method: "POST", headers,
  }, companyID))
}
export function createStatementPaymentInPeriod(projectID: string, companyID: string, jobID: string, itemID: string, periodID: string, revision: string) {
  return jsonHeaders(companyID).then((headers) => requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/items/${encodeURIComponent(itemID)}/payment-period`, {
    method: "POST", headers, body: JSON.stringify({ period_id: periodID, expected_revision: revision }),
  }, companyID))
}
export function createOpeningRegisterInPeriod(projectID: string, companyID: string, jobID: string, itemID: string, periodID: string, revision: string) {
  return jsonHeaders(companyID).then((headers) => requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/items/${encodeURIComponent(itemID)}/opening-period`, {
    method: "POST", headers, body: JSON.stringify({ period_id: periodID, review_revision: revision }),
  }, companyID))
}
export function linkImportedPayment(projectID: string, companyID: string, jobID: string, itemID: string, paymentID: string, revision: string, reason: string) {
  return jsonHeaders(companyID).then((headers) => requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/items/${encodeURIComponent(itemID)}/link-payment`, {
    method: "POST", headers, body: JSON.stringify({ payment_id: paymentID, expected_revision: revision, reason }),
  }, companyID))
}

export async function queueBookImports(projectID: string, companyID: string, fileIDs: string[], target: BookImportKind = "auto", locale = "en") {
  return requestJson<BookImportJob[]>(`/api/projects/${encodeURIComponent(projectID)}/imports`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ file_ids: fileIDs, target, locale }),
  }, companyID)
}

export type AgentProposal = { id: string; operation: string; effect: "draft" | "configuration" | "post" | "lock";
  revision: number; status: "proposed" | "accepted" | "rejected"; stale: boolean; rationale: string;
  period_id: string | null; period_name?: string | null; data: Record<string, unknown>;
  affected_records?: unknown[]; preview?: unknown; result?: { operation_executed: boolean; effect: string; record_id?: string } }
export function listAgentProposals(projectID: string, companyID: string) {
  return boundedRead<AgentProposal[]>(`/api/projects/${encodeURIComponent(projectID)}/agent-proposals`, companyID)
}
export async function decideAgentProposal(projectID: string, companyID: string, proposalID: string, revision: number, accept: boolean) {
  return requestJson<AgentProposal>(`/api/projects/${encodeURIComponent(projectID)}/agent-proposals/${encodeURIComponent(proposalID)}/decision`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ expected_revision: revision, accept }),
  }, companyID)
}
export async function retryBookImport(projectID: string, companyID: string, jobID: string) {
  return requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/retry`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}

export async function cancelBookImport(projectID: string, companyID: string, jobID: string) {
  return requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/cancel`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}

export async function saveBookImportDraft(projectID: string, companyID: string, jobID: string, itemID: string, data: Record<string, unknown>) {
  return requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/items/${encodeURIComponent(itemID)}/draft`, {
    method: "PATCH", headers: await jsonHeaders(companyID), body: JSON.stringify({ data }),
  }, companyID)
}
export async function dismissBillingImportDraft(projectID: string, companyID: string, jobID: string, itemID: string, reviewRevision: string) {
  return requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/items/${encodeURIComponent(itemID)}/dismiss`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ review_revision: reviewRevision }),
  }, companyID)
}
export async function assignBookImportPeriod(projectID: string, companyID: string, jobID: string, periodID: string) {
  return requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/period`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ period_id: periodID }),
  }, companyID)
}
export async function applyBookImportItem(projectID: string, companyID: string, jobID: string, itemID: string) {
  return requestJson<BookImportJob>(`/api/projects/${encodeURIComponent(projectID)}/imports/${encodeURIComponent(jobID)}/items/${encodeURIComponent(itemID)}/apply`, {
    method: "POST", headers: await jsonHeaders(companyID),
  }, companyID)
}

export async function confirmBookAccountDrafts(projectID: string, companyID: string, items: {
  job_id: string; item_id: string; expected_revision: string;
  data: { code: string; name: string; account_type: BookAccount["account_type"] }
}[]) {
  return requestJson<{ items: { job_id: string; item_id: string; account_code: string; reused: boolean }[];
    created: number; reused: number; posted: false }>(`/api/projects/${encodeURIComponent(projectID)}/imports/accounts/confirm`, {
    method: "POST", headers: await jsonHeaders(companyID), body: JSON.stringify({ items }),
  }, companyID)
}

export async function getSessionContextByRemote(remoteSessionID: string, companyID?: string) {
  return requestJson<CoworkSessionContext>(
    `/api/sessions/remote/${remoteSessionID}/context`,
    undefined,
    companyID,
  ).catch(() => undefined)
}

// Execution routing must fail closed on authorization/network errors. The
// optional context helper above is intended for display-only callers.
export function requireSessionContextByRemote(remoteSessionID: string, companyID?: string) {
  return requestJson<CoworkSessionContext>(
    `/api/sessions/remote/${encodeURIComponent(remoteSessionID)}/context`, undefined, companyID,
  )
}

export async function streamMessage(
  projectID: string,
  sessionID: string,
  content: string,
  onEvent: (event: CoworkStreamEvent) => void,
  options?: { applicationLocale?: string; reportOutputLanguage?: string },
) {
  const response = await fetch(
    `${coworkApiBaseUrl()}/api/projects/${projectID}/sessions/${sessionID}/messages/stream`,
    {
      method: "POST",
      headers: await jsonHeaders(),
      body: JSON.stringify({
        content,
        application_locale: options?.applicationLocale,
        report_output_language: options?.reportOutputLanguage,
      }),
    },
  )
  if (!response.ok) throw new Error(await response.text())
  if (!response.body) return

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  const read = async (buffer: string): Promise<void> => {
    const next = await reader.read()
    const chunk = buffer + (next.value ? decoder.decode(next.value, { stream: !next.done }) : "")
    const events = chunk.split("\n\n")
    const remainder = events.pop() ?? ""
    events.forEach((event) => parseSseEvent(event, onEvent))
    if (next.done) {
      parseSseEvent(remainder, onEvent)
      return
    }
    return read(remainder)
  }
  return read("")
}

async function boundedRead<T>(path: string, companyID?: string) {
  const company = companyID || activeCompanyID()
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const error = new DOMException("Request timed out", "TimeoutError")
      controller.abort(error)
      reject(error)
    }, 20_000)
  })
  try {
    return await Promise.race([requestJson<T>(path, { signal: controller.signal }, company), timeout])
  } finally {
    clearTimeout(timer)
  }
}


async function requestJson<T>(
  path: string,
  init?: RequestInit,
  companyID?: string,
  options?: { preserveAuthOnForbidden?: boolean },
) {
  const headers = init?.headers ?? (await authHeaders(false, companyID))
  init?.signal?.throwIfAborted()
  const response = await fetch(`${coworkApiBaseUrl()}${path}`, {
    ...init,
    headers,
  })
  init?.signal?.throwIfAborted()
  if (response.status === 401 && !init?.headers) {
    const headers = await authHeaders(true, companyID)
    init?.signal?.throwIfAborted()
    const retry = await fetch(`${coworkApiBaseUrl()}${path}`, {
      ...init,
      headers,
    })
    init?.signal?.throwIfAborted()
    if (!retry.ok) await handleAuthFailure(retry, init?.signal, options?.preserveAuthOnForbidden)
    if (retry.status === 204) return undefined as T
    return (await retry.json()) as T
  }
  if (!response.ok) await handleAuthFailure(response, init?.signal, options?.preserveAuthOnForbidden)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

async function handleAuthFailure(
  response: Response,
  signal?: AbortSignal | null,
  _preserveAuthOnForbidden = false,
): Promise<never> {
  const text = await response.text()
  signal?.throwIfAborted()
  // Project, company, and agent access can be denied without invalidating the sign-in.
  if (response.status === 401) {
    try {
      sessionStorage.removeItem("cowork.activeCompanyId")
    } catch {
      // Continue clearing the persisted session if tab storage is unavailable.
    }
    try {
      localStorage.removeItem("cowork.auth.token")
      localStorage.removeItem("cowork.auth.email")
      localStorage.removeItem("cowork.auth.name")
      localStorage.removeItem("activeCompanyId")
      localStorage.removeItem("cowork.agent.companyId")
      localStorage.removeItem("cowork.agent.userId")
      localStorage.removeItem("cowork.agent.userEmail")
    } catch {
      // Ignore storage failures and surface the original API error below.
    }
    window.location.assign("/")
  }
  let message = text || "Request failed"
  let detail: unknown
  try {
    const payload = JSON.parse(text) as { detail?: unknown }
    detail = payload.detail
    if (typeof payload.detail === "string" && payload.detail.trim()) message = payload.detail
  } catch {
    // Keep the plain response text when the API did not return JSON.
  }
  throw new CoworkApiError(response.status, message, detail)
}

function parseSseEvent(raw: string, onEvent: (event: CoworkStreamEvent) => void) {
  const data = raw
    .split("\n")
    .filter((line) => line.startsWith("data: "))
    .map((line) => line.slice(6))
    .join("\n")
  if (!data || data === "[DONE]") return
  onEvent(JSON.parse(data) as CoworkStreamEvent)
}

function downloadFilename(contentDisposition: string | null) {
  if (!contentDisposition) return ""
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)
  if (utf8?.[1]) return decodeURIComponent(utf8[1])
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition)
  return match?.[1] ?? ""
}

async function authHeaders(forceRefresh = false, companyID?: string): Promise<Record<string, string>> {
  if (localDevAuthEnabled()) {
    return { ...localDevHeaders(), "X-Active-Company-Id": companyID || activeCompanyID() }
  }
  const token = tokenProvider ? await tokenProvider(forceRefresh) : ""
  if (!token) return {}
  return {
    Authorization: `Bearer ${token}`,
    "X-Active-Company-Id": companyID || activeCompanyID(),
  }
}

function localDevAuthEnabled() {
  return import.meta.env.VITE_LOCAL_DEV_AUTH === "true"
}
import { activeCompanyID, localDevHeaders } from "@/cowork/local-auth"
